import {
    Column,
    DataTable,
    createChunkDataPool,
    logger as splatTransformLogger,
    MemoryFileSystem,
    Transform,
    WebPCodec,
    WorkerQueue,
    writeHtml,
    writeSog as writeSogInternal,
    ZipFileSystem,
    type FileSystem,
    type LogEvent,
    type Renderer,
    type Writer
} from '@playcanvas/splat-transform';
import {
    GSplatData,
    Mat3,
    Mat4,
    PIXELFORMAT_BGRA8,
    Quat,
    Texture,
    Vec3,
    WebgpuGraphicsDevice
} from 'playcanvas';

import { version } from '../package.json';
import { ColorGrade, dcDecode, dcEncode, sigmoid } from './color-grade';
import { Events } from './events';
import { BrowserFileSystem, dataTableToGSplatData, gatherSourceRowsToDataTable, loadLodDataTable, openLodSource, ProgressWriter } from './io';
import {
    NANOGS_NODE_CAP,
    NANOGS_NODE_MIN,
    defaultSimplifyOpts,
    simplifyNodeBatched,
    type ColumnLookup,
    type SimplifyOpts
} from './nanogs';
import type { PagedLodEditSession } from './paged-lod-edit';
import { SHRotation } from './sh-utils';
import { Splat } from './splat';
import { State } from './splat-state';

type SerializeSettings = {
    maxSHBands?: number;            // specifies the maximum number of bands to be exported
    selected?: boolean;             // only export selected gaussians. used for copy/paste
    minOpacity?: number;            // filter out gaussians with alpha less than or equal to minAlpha
    removeInvalid?: boolean;        // filter out gaussians with invalid data (NaN/Infinity)

    // the following options are used when serializing the PLY for document save
    // and are only supported by serializePly
    keepStateData?: boolean;        // keep the state data array
    preserveDeleted?: boolean;      // keep deleted gaussians in vertex data (for index consistency)
    keepWorldTransform?: boolean;   // don't apply the world transform when resolving splat transforms
    keepColorTint?: boolean;        // refrain from applying color tints
    skipPlyRotation?: boolean;      // skip the 180° Z rotation for PLY coordinate system (used for in-memory merge)
};

type AnimTrack = {
    name: string,
    duration: number,
    frameRate: number,
    loopMode: 'none' | 'repeat' | 'pingpong',
    interpolation: 'step' | 'spline',
    smoothness: number,
    keyframes: {
        times: number[],
        values: {
            position: number[],
            target: number[],
            fov: number[],
        }
    }
};

type CameraPose = {
    position: [number, number, number],
    target: [number, number, number],
    fov: number
};

type Camera = {
    initial: CameraPose,
};

type Annotation = {
    position: [number, number, number],
    title: string,
    text: string,
    extras: any,
    camera: Camera
};

type PostEffectSettings = {
    sharpness: {
        enabled: boolean,
        amount: number,
    },
    bloom: {
        enabled: boolean,
        intensity: number,
        blurLevel: number,
    },
    grading: {
        enabled: boolean,
        brightness: number,
        contrast: number,
        saturation: number,
        tint: [number, number, number],
    },
    vignette: {
        enabled: boolean,
        intensity: number,
        inner: number,
        outer: number,
        curvature: number,
    },
    fringing: {
        enabled: boolean,
        intensity: number
    }
};

const defaultPostEffectSettings: PostEffectSettings = {
    sharpness: { enabled: false, amount: 0 },
    bloom: { enabled: false, intensity: 1, blurLevel: 2 },
    grading: { enabled: false, brightness: 1, contrast: 1, saturation: 1, tint: [1, 1, 1] },
    vignette: { enabled: false, intensity: 0.5, inner: 0.3, outer: 0.75, curvature: 1 },
    fringing: { enabled: false, intensity: 0.5 }
};

type ExperienceSettings = {
    version: 2,
    tonemapping: 'none' | 'linear' | 'filmic' | 'hejl' | 'aces' | 'aces2' | 'neutral',
    highPrecisionRendering: boolean,
    soundUrl?: string,
    background: {
        color: [number, number, number],
        skyboxUrl?: string
    },
    postEffectSettings: PostEffectSettings,
    animTracks: AnimTrack[],
    cameras: Camera[],
    annotations: Annotation[],
    startMode: 'default' | 'animTrack' | 'annotation'
};

type ViewerExportSettings = {
    type: 'html' | 'zip';
    experienceSettings: ExperienceSettings;
    events?: Events;
};

type ProgressFunc = (loaded: number, total: number) => void;

const generatedByString = `Generated by ReSplat ${version}`;

// create a filter for gaussians
class GaussianFilter {
    set: (splat: Splat) => void;
    test: (i: number) => boolean;

    constructor(serializeSettings: SerializeSettings) {
        let splat: Splat = null;
        let state: Uint8Array = null;
        let opacity: Float32Array = null;

        this.set = (s: Splat) => {
            splat = s;
            state = splat.splatData.getProp('state') as Uint8Array;
            opacity = splat.splatData.getProp('opacity') as Float32Array;
        };

        const onlySelected = serializeSettings.selected ?? false;
        const minOpacity = serializeSettings.minOpacity ?? 0;
        const removeInvalid = serializeSettings.removeInvalid ?? false;
        const keepStateData = serializeSettings.keepStateData ?? false;
        const preserveDeleted = serializeSettings.preserveDeleted ?? false;

        // properties where +Infinity and -Infinity are valid values
        const infOk = new Set(['opacity']);
        // properties where -Infinity is a valid value
        const negInfOk = new Set(['scale_0', 'scale_1', 'scale_2']);

        this.test = (i: number) => {
            // splat is deleted, skip unless preserving state data (for index validity)
            // or explicitly preserving deleted gaussians
            if (!keepStateData && !preserveDeleted && (state[i] & State.deleted) !== 0) {
                return false;
            }

            // optionally filter out unselected gaussians
            if (onlySelected && (state[i] !== State.selected)) {
                return false;
            }

            // optionally filter based on opacity
            if (minOpacity > 0 && sigmoid(opacity[i]) < minOpacity) {
                return false;
            }

            if (removeInvalid) {
                const { splatData } = splat;

                // check if any property of the gaussian is NaN/Infinity
                const element = splatData.getElement('vertex');
                for (let k = 0; k < element.properties.length; ++k) {
                    const prop = element.properties[k];
                    const { storage, name } = prop;
                    if (storage && !Number.isFinite(storage[i])) {
                        if (storage[i] === -Infinity && (infOk.has(name) || negInfOk.has(name))) continue;
                        if (storage[i] === Infinity && infOk.has(name)) continue;
                        return false;
                    }
                }
            }

            return true;
        };
    }
}

// count the total number of gaussians given a filter
const countGaussians = (splats: Splat[], filter: GaussianFilter) => {
    return splats.reduce((accum, splat) => {
        filter.set(splat);
        for (let i = 0; i < splat.splatData.numSplats; ++i) {
            accum += filter.test(i) ? 1 : 0;
        }
        return accum;
    }, 0);
};

const getVertexProperties = (splatData: GSplatData) => {
    return new Set<string>(
        splatData.getElement('vertex')
        .properties.filter((p: any) => p.storage)
        .map((p: any) => p.name)
    );
};

const getCommonPropNames = (splats: Splat[]) => {
    let result: Set<string>;

    for (let i = 0; i < splats.length; ++i) {
        const props = getVertexProperties(splats[i].splatData);
        result = i === 0 ? props : new Set([...result].filter(i => props.has(i)));
    }

    return [...result];
};

const getCommonProps = (splats: Splat[]) => {
    const result = new Map<string, Set<string>>();  // map of name->type

    for (let i = 0; i < splats.length; ++i) {
        const properties = splats[i].splatData.getElement('vertex').properties.filter((p: any) => p.storage);
        properties.forEach((p: any) => {
            if (result.has(p.name)) {
                result.get(p.name).add(p.type);
            } else {
                result.set(p.name, new Set([p.type]));
            }
        });
    }

    return [...result].filter(([_, v]) => v.size === 1).map(([name, type]) => {
        return { name, type: type.values().next().value };
    });
};

const shNames = new Array(45).fill('').map((_, i) => `f_rest_${i}`);
const shBandCoeffs = [0, 3, 8, 15];

// determine the number of sh bands present given an object with 'f_rest_*' properties
const calcSHBands = (data: Set<string>) => {
    return { '9': 1, '24': 2, '-1': 3 }[shNames.findIndex(v => !data.has(v))] ?? 0;
};

type DataType = 'char' | 'uchar' | 'short' | 'ushort' | 'int' | 'uint' | 'float' | 'double';

const DataTypeSize = (dataType: DataType) => {
    return {
        char: 1,
        uchar: 1,
        short: 2,
        ushort: 2,
        int: 4,
        uint: 4,
        float: 4,
        double: 8
    }[dataType];
};

const v = new Vec3();
const q = new Quat();

// calculate splat transforms on demand and cache the result for next time
class SplatTransformCache {
    getMat: (index: number) => Mat4;
    getRot: (index: number) => Quat;
    getScale: (index: number) => Vec3;
    getSHRot: (index: number) => SHRotation;

    constructor(splat: Splat, keepWorldTransform = false, skipPlyRotation = false) {
        const transforms = new Map<number, { transformIndex: number, mat: Mat4, rot: Quat, scale: Vec3, shRot: SHRotation }>();
        const indices = splat.transformTexture.getSource() as unknown as Uint32Array;
        const tmpMat = new Mat4();
        const tmpMat3 = new Mat3();
        const tmpQuat = new Quat();

        const getTransform = (index: number) => {
            const transformIndex = indices?.[index] ?? 0;
            let result = transforms.get(transformIndex);
            if (!result) {
                result = { transformIndex, mat: null, rot: null, scale: null, shRot: null };
                transforms.set(transformIndex, result);
            }
            return result;
        };

        this.getMat = (index: number) => {
            const transform = getTransform(index);

            if (!transform.mat) {
                const mat = new Mat4();

                // we must undo the transform we apply at load time to output data
                if (!keepWorldTransform) {
                    if (!skipPlyRotation) {
                        mat.setFromEulerAngles(0, 0, -180);
                    }
                    mat.mul2(mat, splat.entity.getWorldTransform());
                }

                // combine with transform palette matrix
                if (transform.transformIndex > 0) {
                    splat.transformPalette.getTransform(transform.transformIndex, tmpMat);
                    mat.mul2(mat, tmpMat);
                }

                transform.mat = mat;
            }

            return transform.mat;
        };

        this.getRot = (index: number) => {
            const transform = getTransform(index);

            if (!transform.rot) {
                transform.rot = new Quat().setFromMat4(this.getMat(index));
            }

            return transform.rot;
        };

        this.getScale = (index: number) => {
            const transform = getTransform(index);

            if (!transform.scale) {
                const scale = new Vec3();
                this.getMat(index).getScale(scale);
                transform.scale = scale;
            }

            return transform.scale;
        };

        this.getSHRot = (index: number) => {
            const transform = getTransform(index);

            if (!transform.shRot) {
                tmpQuat.setFromMat4(this.getMat(index));
                tmpMat3.setFromQuat(tmpQuat);
                transform.shRot = new SHRotation(tmpMat3);
            }

            return transform.shRot;
        };
    }
}

// helper class for extracting and transforming a single splat's data
// to prepare it for export
class SingleSplat {
    // final data keyed on member name
    data: any = {};

    // read a single gaussian's data and transform it for export
    read: (splats: Splat, i: number) => void;

    // specify the data members required
    constructor(members: string[], serializeSettings: SerializeSettings) {
        const data: any = {};
        members.forEach((name) => {
            data[name] = 0;
        });

        const hasPosition = ['x', 'y', 'z'].every(v => data.hasOwnProperty(v));
        const hasRotation = ['rot_0', 'rot_1', 'rot_2', 'rot_3'].every(v => data.hasOwnProperty(v));
        const hasScale = ['scale_0', 'scale_1', 'scale_2'].every(v => data.hasOwnProperty(v));
        const hasColor = ['f_dc_0', 'f_dc_1', 'f_dc_2'].every(v => data.hasOwnProperty(v));
        const hasOpacity = data.hasOwnProperty('opacity');

        const dstSHBands = calcSHBands(new Set(Object.keys(data)));
        const dstSHCoeffs = shBandCoeffs[dstSHBands];
        const tmpSHData = dstSHBands ? new Float32Array(dstSHCoeffs) : null;

        type CacheEntry = {
            splat: Splat;
            transformCache: SplatTransformCache;
            srcProps: { [name: string]: Float32Array };
            grade: ColorGrade;
        };

        const cacheMap = new Map<Splat, CacheEntry>();
        let cacheEntry: CacheEntry;

        const read = (splat: Splat, i: number) => {
            // get the cached data entry for this splat
            if (splat !== cacheEntry?.splat) {
                if (!cacheMap.has(splat)) {
                    const transformCache = new SplatTransformCache(splat, serializeSettings.keepWorldTransform, serializeSettings.skipPlyRotation);

                    const srcPropNames = getVertexProperties(splat.splatData);
                    const srcSHBands = calcSHBands(srcPropNames);
                    const srcSHCoeffs = shBandCoeffs[srcSHBands];

                    // cache the props objects
                    const srcProps: { [name: string]: Float32Array } = {};

                    members.forEach((name) => {
                        const shIndex = shNames.indexOf(name);
                        if (shIndex >= 0) {
                            const a = Math.floor(shIndex / dstSHCoeffs);
                            const b = shIndex % dstSHCoeffs;
                            srcProps[name] = (b < srcSHCoeffs) ? splat.splatData.getProp(shNames[a * srcSHCoeffs + b]) as Float32Array : null;
                        } else {
                            srcProps[name] = splat.splatData.getProp(name) as Float32Array;
                        }
                    });

                    const grade = new ColorGrade(splat);

                    cacheEntry = { splat, transformCache, srcProps, grade };

                    cacheMap.set(splat, cacheEntry);
                } else {
                    cacheEntry = cacheMap.get(splat);
                }
            }

            const { transformCache, srcProps, grade } = cacheEntry;

            // copy members
            members.forEach((name) => {
                data[name] = srcProps[name]?.[i] ?? 0;
            });

            // apply transform palette transforms
            const mat = transformCache.getMat(i);

            if (hasPosition) {
                v.set(data.x, data.y, data.z);
                mat.transformPoint(v, v);
                [data.x, data.y, data.z] = [v.x, v.y, v.z];
            }

            if (hasRotation) {
                const quat = transformCache.getRot(i);
                q.set(data.rot_1, data.rot_2, data.rot_3, data.rot_0).mul2(quat, q);
                [data.rot_1, data.rot_2, data.rot_3, data.rot_0] = [q.x, q.y, q.z, q.w];
            }

            if (hasScale) {
                const scale = transformCache.getScale(i);
                data.scale_0 = Math.log(Math.exp(data.scale_0) * scale.x);
                data.scale_1 = Math.log(Math.exp(data.scale_1) * scale.y);
                data.scale_2 = Math.log(Math.exp(data.scale_2) * scale.z);
            }

            if (dstSHBands > 0) {
                for (let c = 0; c < 3; ++c) {
                    for (let d = 0; d < dstSHCoeffs; ++d) {
                        tmpSHData[d] = data[shNames[c * dstSHCoeffs + d]];
                    }

                    transformCache.getSHRot(i).apply(tmpSHData);

                    for (let d = 0; d < dstSHCoeffs; ++d) {
                        data[shNames[c * dstSHCoeffs + d]] = tmpSHData[d];
                    }
                }
            }

            if (!serializeSettings.keepColorTint && hasColor && grade.hasTint) {
                const c = {
                    r: dcDecode(data.f_dc_0),
                    g: dcDecode(data.f_dc_1),
                    b: dcDecode(data.f_dc_2)
                };

                grade.applyDC(c);
                data.f_dc_0 = dcEncode(c.r);
                data.f_dc_1 = dcEncode(c.g);
                data.f_dc_2 = dcEncode(c.b);

                if (dstSHBands > 0) {
                    for (let d = 0; d < dstSHCoeffs; ++d) {
                        c.r = data[shNames[d]];
                        c.g = data[shNames[d + dstSHCoeffs]];
                        c.b = data[shNames[d + dstSHCoeffs * 2]];

                        grade.applySH(c);
                        data[shNames[d]] = c.r;
                        data[shNames[d + dstSHCoeffs]] = c.g;
                        data[shNames[d + dstSHCoeffs * 2]] = c.b;
                    }
                }
            }

            if (!serializeSettings.keepColorTint && hasOpacity && splat.transparency !== 1) {
                data.opacity = grade.applyOpacity(data.opacity);
            }
        };

        this.data = data;
        this.read = read;
    }
}

const serializePlyToWriter = async (splats: Splat[], serializeSettings: SerializeSettings, writer: Writer, progress?: ProgressFunc): Promise<void> => {
    const { maxSHBands, keepStateData } = serializeSettings;

    // create filter and count total gaussians
    const filter = new GaussianFilter(serializeSettings);
    const totalGaussians = countGaussians(splats, filter);
    if (totalGaussians === 0) {
        return;
    }

    // this data is filtered out, as it holds internal editor state
    const internalProps = keepStateData ? ['transform'] : ['state', 'transform'];

    const props = getCommonProps(splats)
    // filter out internal props
    .filter(p => !internalProps.includes(p.name))
    // filter out max SH bands
    .filter((p) => {
        if (!p.name.startsWith('f_rest_')) {
            return true;
        }
        const i = parseInt(p.name.slice(7), 10);
        return i < [0, 9, 24, 45][maxSHBands ?? 3];
    });

    const headerText = [
        'ply',
        'format binary_little_endian 1.0',
        // FIXME: disable for now due to other tooling not supporting any header
        // `comment ${generatedByString}`,
        `element vertex ${totalGaussians}`,
        props.map(p => `property ${p.type} ${p.name}`),
        'end_header',
        ''
    ].flat().join('\n');

    const singleSplat = new SingleSplat(props.map(p => p.name), serializeSettings);

    const gaussianSizeBytes = props.reduce((tot, p) => tot + DataTypeSize(p.type as DataType), 0);

    const buf = new Uint8Array(1024 * gaussianSizeBytes);
    const dataView = new DataView(buf.buffer);
    let offset = 0;

    const header = new TextEncoder().encode(headerText);

    // construct a progress writer over the writer
    const progressWriter = new ProgressWriter(writer, header.byteLength + totalGaussians * gaussianSizeBytes, progress);

    // write encoded header
    await progressWriter.write(header);

    for (let e = 0; e < splats.length; ++e) {
        const splat = splats[e];
        const { splatData } = splat;
        filter.set(splat);

        for (let i = 0; i < splatData.numSplats; ++i) {
            if (!filter.test(i)) continue;

            singleSplat.read(splat, i);

            // write
            for (let j = 0; j < props.length; ++j) {
                if (props[j].type === 'uchar') {
                    dataView.setUint8(offset, singleSplat.data[props[j].name]);
                    offset += 1;
                } else {
                    dataView.setFloat32(offset, singleSplat.data[props[j].name], true);
                    offset += 4;
                }
            }

            // buffer is full, write it to the output stream
            if (offset === buf.byteLength) {
                await progressWriter.write(buf);
                offset = 0;
            }
        }
    }

    // write the last (most likely partially filled) buf
    if (offset > 0) {
        await progressWriter.write(new Uint8Array(buf.buffer, 0, offset));
    }

    progressWriter.close();
};

const serializePly = async (splats: Splat[], serializeSettings: SerializeSettings, fs: FileSystem, filename = 'output.ply', progress?: ProgressFunc): Promise<void> => {
    const writer = await fs.createWriter(filename);
    await serializePlyToWriter(splats, serializeSettings, writer, progress);
    await writer.close();
};

// Stream an edited LOD0 from a paged proxy session as a regular PLY. The
// session pages are loaded twice: the first pass counts surviving rows for a
// correct PLY header, and the second pass writes one page's payload at a time.
// No full LOD0 GSplat is created in the renderer.
const serializePagedLod0Ply = async (
    splat: Splat,
    session: PagedLodEditSession,
    serializeSettings: SerializeSettings,
    fs: FileSystem,
    filename = 'output.ply'
): Promise<void> => {
    const preparePageSplat = (loaded: Awaited<ReturnType<PagedLodEditSession['loadPage']>>): Splat => {
        const data = loaded.gsplatData;
        const element = data.getElement('vertex');
        element.properties = element.properties.filter((property: any) => property.name !== 'source_index');
        let state = data.getProp('state') as Uint8Array | undefined;
        if (!state) {
            state = new Uint8Array(data.numSplats);
            element.properties.push({ type: 'uchar', name: 'state', storage: state, byteSize: 1 });
        }
        for (let i = 0; i < loaded.sourceIndices.length; i++) {
            if (session.isSourceDeleted(loaded.sourceIndices[i])) state[i] |= State.deleted;
        }
        return {
            splatData: data,
            numSplats: data.numSplats,
            transformTexture: { getSource: (): null => null },
            transformPalette: null,
            entity: splat.entity,
            tintClr: splat.tintClr,
            temperature: splat.temperature,
            saturation: splat.saturation,
            brightness: splat.brightness,
            blackPoint: splat.blackPoint,
            whitePoint: splat.whitePoint,
            transparency: splat.transparency
        } as unknown as Splat;
    };

    const filter = new GaussianFilter(serializeSettings);
    let total = 0;
    for (const page of session.manifest.pages) {
        const loaded = await session.loadPage(page);
        try {
            const pageSplat = preparePageSplat(loaded);
            filter.set(pageSplat);
            for (let i = 0; i < pageSplat.splatData.numSplats; i++) {
                if (filter.test(i)) total++;
            }
        } finally {
            session.releasePages([page.id]);
        }
    }
    if (total === 0) return;

    const writer = await fs.createWriter(filename);
    try {
        let wroteHeader = false;
        const encoder = new TextEncoder();
        for (const page of session.manifest.pages) {
            const loaded = await session.loadPage(page);
            try {
                const pageSplat = preparePageSplat(loaded);
                const chunks: Uint8Array[] = [];
                const memoryWriter = {
                    bytesWritten: 0,
                    write(data: Uint8Array) {
                        chunks.push(data);
                        this.bytesWritten += data.length;
                        return Promise.resolve();
                    },
                    close() {
                        return Promise.resolve();
                    },
                    abort() {}
                };
                await serializePlyToWriter([pageSplat], serializeSettings, memoryWriter);
                const pageBytes = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
                let offset = 0;
                for (const chunk of chunks) {
                    pageBytes.set(chunk, offset);
                    offset += chunk.length;
                }
                const headerMarker = encoder.encode('end_header\n');
                let headerEnd = -1;
                for (let i = 0; i <= pageBytes.length - headerMarker.length; i++) {
                    let match = true;
                    for (let j = 0; j < headerMarker.length; j++) {
                        if (pageBytes[i + j] !== headerMarker[j]) {
                            match = false;
                            break;
                        }
                    }
                    if (match) {
                        headerEnd = i + headerMarker.length;
                        break;
                    }
                }
                if (headerEnd < 0) throw new Error('Paged PLY page has no header');
                if (!wroteHeader) {
                    const pageHeader = new TextDecoder().decode(pageBytes.subarray(0, headerEnd));
                    const header = pageHeader.replace(/element vertex \d+/, `element vertex ${total}`);
                    await writer.write(encoder.encode(header));
                    wroteHeader = true;
                }
                await writer.write(pageBytes.subarray(headerEnd));
            } finally {
                session.releasePages([page.id]);
            }
        }
    } finally {
        await writer.close();
    }
};

// Standard PLY format: 62 properties in a specific order
// This format is compatible with most Gaussian Splat renderers
const STANDARD_PLY_PROPERTIES = [
    // Position (3)
    'x', 'y', 'z',
    // Normals (3) - filled with 0 if missing
    'nx', 'ny', 'nz',
    // Spherical Harmonics DC (3)
    'f_dc_0', 'f_dc_1', 'f_dc_2',
    // Spherical Harmonics Rest (45) - filled with 0 if missing
    ...new Array(45).fill('').map((_, i) => `f_rest_${i}`),
    // Opacity (1)
    'opacity',
    // Scale (3)
    'scale_0', 'scale_1', 'scale_2',
    // Rotation (4)
    'rot_0', 'rot_1', 'rot_2', 'rot_3'
];

const serializeStandardPly = async (splats: Splat[], serializeSettings: SerializeSettings, fs: FileSystem, filename = 'output.ply', progress?: ProgressFunc): Promise<void> => {
    const { maxSHBands } = serializeSettings;

    // create filter and count total gaussians
    const filter = new GaussianFilter(serializeSettings);
    const totalGaussians = countGaussians(splats, filter);
    if (totalGaussians === 0) {
        return;
    }

    // Determine which f_rest properties to include based on maxSHBands
    const maxSHCoeffs = [0, 9, 24, 45][maxSHBands ?? 3];

    // Build properties list - always include all standard properties
    // but respect maxSHBands for f_rest
    const props: { name: string; type: string }[] = [];
    for (const name of STANDARD_PLY_PROPERTIES) {
        // Check if this is an f_rest property beyond our band limit
        if (name.startsWith('f_rest_')) {
            const idx = parseInt(name.slice(7), 10);
            if (idx >= maxSHCoeffs) {
                continue; // Skip f_rest beyond selected bands
            }
        }
        props.push({ name, type: 'float' });
    }

    // Build header
    const headerText = [
        'ply',
        'format binary_little_endian 1.0',
        `element vertex ${totalGaussians}`,
        props.map(p => `property ${p.type} ${p.name}`),
        'end_header',
        ''
    ].flat().join('\n');

    // Get the property names that exist in the source data
    const commonPropNames = new Set(getCommonPropNames(splats));

    // Create SingleSplat reader for properties that exist in source
    // We need to request all properties we want to read
    const propsToRead = props.map(p => p.name).filter((name) => {
        // Always try to read these core properties
        if (['x', 'y', 'z', 'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
            'scale_0', 'scale_1', 'scale_2',
            'rot_0', 'rot_1', 'rot_2', 'rot_3'].includes(name)) {
            return true;
        }
        // For normals and f_rest, only read if they exist
        return commonPropNames.has(name);
    });

    const singleSplat = new SingleSplat(propsToRead, serializeSettings);

    const gaussianSizeBytes = props.length * 4; // all properties are float32

    const buf = new Uint8Array(1024 * gaussianSizeBytes);
    const dataView = new DataView(buf.buffer);
    let offset = 0;

    const header = new TextEncoder().encode(headerText);

    // create writer from filesystem
    const writer = await fs.createWriter(filename);

    // construct a progress writer over the writer
    const progressWriter = new ProgressWriter(writer, header.byteLength + totalGaussians * gaussianSizeBytes, progress);

    // write encoded header
    await progressWriter.write(header);

    for (let e = 0; e < splats.length; ++e) {
        const splat = splats[e];
        const { splatData } = splat;
        filter.set(splat);

        for (let i = 0; i < splatData.numSplats; ++i) {
            if (!filter.test(i)) continue;

            singleSplat.read(splat, i);

            // write all properties in standard order
            for (let j = 0; j < props.length; ++j) {
                const propName = props[j].name;
                // Use value from source if available, otherwise 0
                const value = Object.prototype.hasOwnProperty.call(singleSplat.data, propName) ? singleSplat.data[propName] : 0;
                dataView.setFloat32(offset, value, true);
                offset += 4;
            }

            // buffer is full, write it to the output stream
            if (offset === buf.byteLength) {
                await progressWriter.write(buf);
                offset = 0;
            }
        }
    }

    // write the last (most likely partially filled) buf
    if (offset > 0) {
        await progressWriter.write(new Uint8Array(buf.buffer, 0, offset));
    }

    progressWriter.close();
    await writer.close();
};

interface CompressedIndex {
    splatIndex: number;
    i: number;
    globalIndex: number;
}

// process and compress a chunk of 256 splats
class Chunk {
    static members = [
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
        'rot_0', 'rot_1', 'rot_2', 'rot_3'
    ];

    size: number;
    data: any = {};

    // compressed data
    position: Uint32Array;
    rotation: Uint32Array;
    scale: Uint32Array;
    color: Uint32Array;

    constructor(size = 256) {
        this.size = size;
        Chunk.members.forEach((m) => {
            this.data[m] = new Float32Array(size);
        });
        this.position = new Uint32Array(size);
        this.rotation = new Uint32Array(size);
        this.scale = new Uint32Array(size);
        this.color = new Uint32Array(size);
    }

    set(index: number, splat: SingleSplat) {
        Chunk.members.forEach((name) => {
            this.data[name][index] = splat.data[name];
        });
    }

    pack() {
        const calcMinMax = (data: Float32Array) => {
            let min;
            let max;
            min = max = data[0];
            for (let i = 1; i < data.length; ++i) {
                const v = data[i];
                min = Math.min(min, v);
                max = Math.max(max, v);
            }
            return { min, max };
        };

        const normalize = (x: number, min: number, max: number) => {
            if (x <= min) return 0;
            if (x >= max) return 1;
            return (max - min < 0.00001) ? 0 : (x - min) / (max - min);
        };

        const data = this.data;

        const x = data.x;
        const y = data.y;
        const z = data.z;
        const scale_0 = data.scale_0;
        const scale_1 = data.scale_1;
        const scale_2 = data.scale_2;
        const rot_0 = data.rot_0;
        const rot_1 = data.rot_1;
        const rot_2 = data.rot_2;
        const rot_3 = data.rot_3;
        const f_dc_0 = data.f_dc_0;
        const f_dc_1 = data.f_dc_1;
        const f_dc_2 = data.f_dc_2;
        const opacity = data.opacity;

        const px = calcMinMax(x);
        const py = calcMinMax(y);
        const pz = calcMinMax(z);

        const sx = calcMinMax(scale_0);
        const sy = calcMinMax(scale_1);
        const sz = calcMinMax(scale_2);

        // clamp scale because sometimes values are at infinity
        const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
        sx.min = clamp(sx.min, -20, 20);
        sx.max = clamp(sx.max, -20, 20);
        sy.min = clamp(sy.min, -20, 20);
        sy.max = clamp(sy.max, -20, 20);
        sz.min = clamp(sz.min, -20, 20);
        sz.max = clamp(sz.max, -20, 20);

        // convert f_dc_ to colors before calculating min/max and packaging
        for (let i = 0; i < f_dc_0.length; ++i) {
            f_dc_0[i] = dcDecode(f_dc_0[i]);
            f_dc_1[i] = dcDecode(f_dc_1[i]);
            f_dc_2[i] = dcDecode(f_dc_2[i]);
        }

        const cr = calcMinMax(f_dc_0);
        const cg = calcMinMax(f_dc_1);
        const cb = calcMinMax(f_dc_2);

        const packUnorm = (value: number, bits: number) => {
            const t = (1 << bits) - 1;
            return Math.max(0, Math.min(t, Math.floor(value * t + 0.5)));
        };

        const pack111011 = (x: number, y: number, z: number) => {
            return packUnorm(x, 11) << 21 |
                   packUnorm(y, 10) << 11 |
                   packUnorm(z, 11);
        };

        const pack8888 = (x: number, y: number, z: number, w: number) => {
            return packUnorm(x, 8) << 24 |
                   packUnorm(y, 8) << 16 |
                   packUnorm(z, 8) << 8 |
                   packUnorm(w, 8);
        };

        // pack quaternion into 2,10,10,10
        const packRot = (x: number, y: number, z: number, w: number) => {
            q.set(x, y, z, w).normalize();
            const a = [q.x, q.y, q.z, q.w];
            const largest = a.reduce((curr, v, i) => (Math.abs(v) > Math.abs(a[curr]) ? i : curr), 0);

            if (a[largest] < 0) {
                a[0] = -a[0];
                a[1] = -a[1];
                a[2] = -a[2];
                a[3] = -a[3];
            }

            const norm = Math.sqrt(2) * 0.5;
            let result = largest;
            for (let i = 0; i < 4; ++i) {
                if (i !== largest) {
                    result = (result << 10) | packUnorm(a[i] * norm + 0.5, 10);
                }
            }

            return result;
        };

        // pack
        for (let i = 0; i < this.size; ++i) {
            this.position[i] = pack111011(
                normalize(x[i], px.min, px.max),
                normalize(y[i], py.min, py.max),
                normalize(z[i], pz.min, pz.max)
            );

            this.rotation[i] = packRot(rot_0[i], rot_1[i], rot_2[i], rot_3[i]);

            this.scale[i] = pack111011(
                normalize(scale_0[i], sx.min, sx.max),
                normalize(scale_1[i], sy.min, sy.max),
                normalize(scale_2[i], sz.min, sz.max)
            );

            this.color[i] = pack8888(
                normalize(f_dc_0[i], cr.min, cr.max),
                normalize(f_dc_1[i], cg.min, cg.max),
                normalize(f_dc_2[i], cb.min, cb.max),
                1 / (1 + Math.exp(-opacity[i]))
            );
        }

        return { px, py, pz, sx, sy, sz, cr, cg, cb };
    }
}

// sort the compressed indices into morton order
const sortSplats = (splats: Splat[], indices: CompressedIndex[]) => {
    // https://fgiesen.wordpress.com/2009/12/13/decoding-morton-codes/
    const encodeMorton3 = (x: number, y: number, z: number) : number => {
        const Part1By2 = (x: number) => {
            x &= 0x000003ff;
            x = (x ^ (x << 16)) & 0xff0000ff;
            x = (x ^ (x <<  8)) & 0x0300f00f;
            x = (x ^ (x <<  4)) & 0x030c30c3;
            x = (x ^ (x <<  2)) & 0x09249249;
            return x;
        };

        return (Part1By2(z) << 2) + (Part1By2(y) << 1) + Part1By2(x);
    };

    let minx: number;
    let miny: number;
    let minz: number;
    let maxx: number;
    let maxy: number;
    let maxz: number;

    // calculate scene extents across all splats (using sort centers, because they're in world space)
    for (let i = 0; i < splats.length; ++i) {
        const splat = splats[i];
        const splatData = splat.splatData;
        const state = splatData.getProp('state') as Uint8Array;
        const { centers } = splat.entity.gsplat.instance.sorter;

        for (let i = 0; i < splatData.numSplats; ++i) {
            if ((state[i] & State.deleted) === 0) {
                const x = centers[i * 3 + 0];
                const y = centers[i * 3 + 1];
                const z = centers[i * 3 + 2];

                if (minx === undefined) {
                    minx = maxx = x;
                    miny = maxy = y;
                    minz = maxz = z;
                } else {
                    if (x < minx) minx = x; else if (x > maxx) maxx = x;
                    if (y < miny) miny = y; else if (y > maxy) maxy = y;
                    if (z < minz) minz = z; else if (z > maxz) maxz = z;
                }
            }
        }
    }

    const xlen = maxx - minx;
    const ylen = maxy - miny;
    const zlen = maxz - minz;

    const morton = new Uint32Array(indices.length);
    let idx = 0;
    for (let i = 0; i < splats.length; ++i) {
        const splat = splats[i];
        const splatData = splat.splatData;
        const state = splatData.getProp('state') as Uint8Array;
        const { centers } = splat.entity.gsplat.instance.sorter;

        for (let i = 0; i < splatData.numSplats; ++i) {
            if ((state[i] & State.deleted) === 0) {
                const x = centers[i * 3 + 0];
                const y = centers[i * 3 + 1];
                const z = centers[i * 3 + 2];

                const ix = Math.min(1023, Math.floor(1024 * (x - minx) / xlen));
                const iy = Math.min(1023, Math.floor(1024 * (y - miny) / ylen));
                const iz = Math.min(1023, Math.floor(1024 * (z - minz) / zlen));

                morton[idx++] = encodeMorton3(ix, iy, iz);
            }
        }
    }

    // order splats by morton code
    indices.sort((a, b) => morton[a.globalIndex] - morton[b.globalIndex]);
};

const serializePlyCompressed = async (splats: Splat[], options: SerializeSettings, fs: FileSystem, progress?: ProgressFunc): Promise<void> => {
    const { maxSHBands } = options;

    // create filter and count total gaussians
    const filter = new GaussianFilter(options);

    // make a list of indices spanning all splats (so we can sort them together)
    const indices: CompressedIndex[] = [];
    for (let splatIndex = 0; splatIndex < splats.length; ++splatIndex) {
        const splatData = splats[splatIndex].splatData;
        filter.set(splats[splatIndex]);
        for (let i = 0; i < splatData.numSplats; ++i) {
            if (filter.test(i)) {
                indices.push({ splatIndex, i, globalIndex: indices.length });
            }
        }
    }

    if (indices.length === 0) {
        console.error('nothing to export');
        return;
    }

    // create writer from filesystem
    const writer = await fs.createWriter('output.compressed.ply');

    const numSplats = indices.length;
    const numChunks = Math.ceil(numSplats / 256);

    const chunkProps = [
        'min_x', 'min_y', 'min_z',
        'max_x', 'max_y', 'max_z',
        'min_scale_x', 'min_scale_y', 'min_scale_z',
        'max_scale_x', 'max_scale_y', 'max_scale_z',
        'min_r', 'min_g', 'min_b',
        'max_r', 'max_g', 'max_b'
    ];

    const vertexProps = [
        'packed_position',
        'packed_rotation',
        'packed_scale',
        'packed_color'
    ];

    // calculate the number of output bands given the scene splat data and
    // user-chosen maxSHBands
    const outputSHBands = (() => {
        const splatBands = splats.map(s => calcSHBands(getVertexProperties(s.splatData)));
        return Math.min(maxSHBands ?? 3, Math.max(...splatBands));
    })();
    const outputSHCoeffs = shBandCoeffs[outputSHBands];

    const shHeader = outputSHBands ? [
        `element sh ${numSplats}`,
        new Array(outputSHCoeffs * 3).fill('').map((_, i) => `property uchar f_rest_${i}`)
    ].flat() : [];

    const headerText = [
        'ply',
        'format binary_little_endian 1.0',
        `comment ${generatedByString}`,
        `element chunk ${numChunks}`,
        chunkProps.map(p => `property float ${p}`),
        `element vertex ${numSplats}`,
        vertexProps.map(p => `property uint ${p}`),
        shHeader,
        'end_header\n'
    ].flat().join('\n');

    // sort splats into some kind of order (morton order rn)
    sortSplats(splats, indices);

    const singleSplat = new SingleSplat([
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
        'rot_0', 'rot_1', 'rot_2', 'rot_3'
    ], options);

    const prepareChunk = (chunk: Chunk, i: number) => {
        const num = Math.min(numSplats, (i + 1) * 256) - i * 256;

        for (let j = 0; j < num; ++j) {
            const index = indices[i * 256 + j];

            // read splat
            singleSplat.read(splats[index.splatIndex], index.i);

            // update chunk
            chunk.set(j, singleSplat);
        }

        // pad the end of the last chunk with duplicate data
        if (num < 256) {
            for (let j = num; j < 256; ++j) {
                chunk.set(j, singleSplat);
            }
        }
    };

    const header = new TextEncoder().encode(headerText);

    const totalBytes =
        header.byteLength +
        numChunks * chunkProps.length * 4 +
        numSplats * vertexProps.length * 4 +
        outputSHCoeffs * 3 * numSplats;

    const progressWriter = new ProgressWriter(writer, totalBytes, progress);

    // write the header
    await progressWriter.write(header);

    const chunk = new Chunk();

    // write chunks
    const chunkData = new Float32Array(18);
    const chunkDataUint8 = new Uint8Array(chunkData.buffer);
    for (let i = 0; i < numChunks; ++i) {
        prepareChunk(chunk, i);

        const result = chunk.pack();

        chunkData[0] = result.px.min;
        chunkData[1] = result.py.min;
        chunkData[2] = result.pz.min;
        chunkData[3] = result.px.max;
        chunkData[4] = result.py.max;
        chunkData[5] = result.pz.max;

        chunkData[6] = result.sx.min;
        chunkData[7] = result.sy.min;
        chunkData[8] = result.sz.min;
        chunkData[9] = result.sx.max;
        chunkData[10] = result.sy.max;
        chunkData[11] = result.sz.max;

        chunkData[12] = result.cr.min;
        chunkData[13] = result.cg.min;
        chunkData[14] = result.cb.min;
        chunkData[15] = result.cr.max;
        chunkData[16] = result.cg.max;
        chunkData[17] = result.cb.max;

        await progressWriter.write(chunkDataUint8);
    }

    // write vertices
    const vertexData = new Uint32Array(256 * 4);
    const vertexDataUint8 = new Uint8Array(vertexData.buffer);
    for (let i = 0; i < numChunks; ++i) {
        const num = Math.min(numSplats, (i + 1) * 256) - i * 256;

        prepareChunk(chunk, i);
        chunk.pack();

        // write vertex data
        for (let j = 0; j < num; ++j) {
            vertexData[j * 4 + 0] = chunk.position[j];
            vertexData[j * 4 + 1] = chunk.rotation[j];
            vertexData[j * 4 + 2] = chunk.scale[j];
            vertexData[j * 4 + 3] = chunk.color[j];
        }

        await progressWriter.write(num === 256 ? vertexDataUint8 : new Uint8Array(vertexData.buffer, 0, num * 4 * 4));
    }

    // write sh
    const singleSplatSH = new SingleSplat(shNames.slice(0, outputSHCoeffs * 3), options);

    const shData = new Uint8Array(outputSHCoeffs * 3 * 256);
    for (let i = 0; i < numChunks; ++i) {
        const num = Math.min(numSplats, (i + 1) * 256) - i * 256;

        for (let j = 0; j < num; ++j) {
            const index = indices[i * 256 + j];

            // read splat
            singleSplatSH.read(splats[index.splatIndex], index.i);

            // quantize and write sh data
            const offset = j * outputSHCoeffs * 3;
            for (let k = 0; k < outputSHCoeffs * 3; ++k) {
                const nvalue = singleSplatSH.data[shNames[k]] / 8 + 0.5;
                shData[offset + k] = Math.max(0, Math.min(255, Math.trunc(nvalue * 256)));
            }
        }

        await progressWriter.write(num === 256 ? shData : new Uint8Array(shData.buffer, 0, num * outputSHCoeffs * 3));
    }

    progressWriter.close();
    await writer.close();
};

const serializeSplat = async (splats: Splat[], options: SerializeSettings, fs: FileSystem): Promise<void> => {
    // create writer from filesystem
    const writer = await fs.createWriter('output.splat');
    // create filter and count total gaussians
    const filter = new GaussianFilter(options);
    const totalGaussians = countGaussians(splats, filter);
    if (totalGaussians === 0) {
        return;
    }

    // position.xyz: float32, scale.xyz: float32, color.rgba: uint8, quaternion.ijkl: uint8
    const result = new Uint8Array(totalGaussians * 32);
    const dataView = new DataView(result.buffer);

    let idx = 0;

    const props = ['x', 'y', 'z', 'opacity', 'rot_0', 'rot_1', 'rot_2', 'rot_3', 'f_dc_0', 'f_dc_1', 'f_dc_2', 'scale_0', 'scale_1', 'scale_2'];
    const singleSplat = new SingleSplat(props, options);
    const { data } = singleSplat;

    const clamp = (x: number) => Math.max(0, Math.min(255, x));

    for (let e = 0; e < splats.length; ++e) {
        const splat = splats[e];
        const { splatData } = splat;
        filter.set(splat);

        for (let i = 0; i < splatData.numSplats; ++i) {
            if (!filter.test(i)) continue;

            singleSplat.read(splat, i);

            const off = idx++ * 32;

            dataView.setFloat32(off + 0, data.x, true);
            dataView.setFloat32(off + 4, data.y, true);
            dataView.setFloat32(off + 8, data.z, true);

            dataView.setFloat32(off + 12, Math.exp(data.scale_0), true);
            dataView.setFloat32(off + 16, Math.exp(data.scale_1), true);
            dataView.setFloat32(off + 20, Math.exp(data.scale_2), true);

            dataView.setUint8(off + 24, clamp(dcDecode(data.f_dc_0) * 255));
            dataView.setUint8(off + 25, clamp(dcDecode(data.f_dc_1) * 255));
            dataView.setUint8(off + 26, clamp(dcDecode(data.f_dc_2) * 255));
            dataView.setUint8(off + 27, clamp(sigmoid(data.opacity) * 255));

            dataView.setUint8(off + 28, clamp(data.rot_0 * 128 + 128));
            dataView.setUint8(off + 29, clamp(data.rot_1 * 128 + 128));
            dataView.setUint8(off + 30, clamp(data.rot_2 * 128 + 128));
            dataView.setUint8(off + 31, clamp(data.rot_3 * 128 + 128));
        }
    }

    await writer.write(result);
    await writer.close();
};

// Cached WebGPU device for SOG compression
let cachedGpuDevice: WebgpuGraphicsDevice | null = null;
let cachedBackbuffer: Texture | null = null;

// Configure splat-transform's worker/WASM loading for the bundled environment.
//
// The rollup bundle inlines splat-transform's JS into dist/index.js, but the
// worker script (dist/worker.mjs) and WASM binary (lib/webp.wasm) are separate
// assets that splat-transform auto-resolves via `import.meta.url`. rollup does
// NOT rewrite `new Worker(new URL('./worker.mjs', import.meta.url))` (that's a
// Vite/webpack feature), so we must point WorkerQueue.workerUrl at the deployed
// worker asset explicitly.
//
// History: previously this forced WorkerQueue.maxWorkers=0 (inline) because
// Electron loaded the renderer from file://, where module workers cannot spawn
// (CORS) and onerror never fires — stalling the WorkerQueue slot forever.
// Now Electron registers an app:// privileged protocol (see electron/main.cjs
// registerAppProtocol) which enables module workers, fetch(), and service
// workers. So we let maxWorkers auto-size (min(4, cores-1)) for ~3× parallel
// WebP encoding speedup. If a worker spawn fails at runtime, splat-transform
// falls back to inline automatically (per WorkerQueue contract).
let splatTransformConfigured = false;
const configureSplatTransform = () => {
    if (splatTransformConfigured) return;
    splatTransformConfigured = true;

    // Auto-size worker pool: min(4, cores-1). Workers spawn lazily and run
    // encodeWebp/quantize1d tasks off the main thread. Falls back to inline
    // if spawn fails (file:// without app:// protocol, or no Worker support).
    WorkerQueue.maxWorkers = null;

    // Resolve worker.mjs relative to this module (dist/index.js after bundling).
    // rollup.config.mjs copies dist/worker.mjs into dist/ alongside the bundle.
    try {
        WorkerQueue.workerUrl = new URL('./worker.mjs', import.meta.url).toString();
    } catch {
        // import.meta.url may be unavailable in some non-module contexts; leave
        // the default and let splat-transform's own resolution attempt run.
    }

    // Resolve webp.wasm relative to this module (dist/index.js after bundling).
    // rollup.config.mjs copies the wasm into dist/ alongside the bundle.
    try {
        WebPCodec.wasmUrl = new URL('./webp.wasm', import.meta.url).toString();
    } catch {
        // import.meta.url may be unavailable in some non-module contexts; leave
        // the default and let splat-transform's own resolution attempt run.
    }
};

const createGpuDevice = async (): Promise<WebgpuGraphicsDevice> => {
    configureSplatTransform();

    if (cachedGpuDevice) {
        return cachedGpuDevice;
    }

    if (!navigator.gpu) {
        throw new Error('WebGPU is not available in this browser');
    }

    // Create a minimal canvas for the graphics device
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;

    const graphicsDevice = new WebgpuGraphicsDevice(canvas, {
        antialias: false,
        depth: false,
        stencil: false
    });

    await graphicsDevice.createDevice();

    // Create external backbuffer (required by PlayCanvas)
    cachedBackbuffer = new Texture(graphicsDevice, {
        width: 1024,
        height: 512,
        name: 'SogComputeBackbuffer',
        mipmaps: false,
        format: PIXELFORMAT_BGRA8
    });

    // @ts-ignore - externalBackbuffer is an internal property
    graphicsDevice.externalBackbuffer = cachedBackbuffer;

    cachedGpuDevice = graphicsDevice;
    return graphicsDevice;
};

/**
 * Extract Splat data into a DataTable for use with splat-transform writers.
 * This is shared between serializeSog and serializeViewer.
 */
const extractDataTable = (splats: Splat[], settings: SerializeSettings): DataTable => {
    const { maxSHBands = 3 } = settings;

    // Determine which members to extract
    const shCoeffs = [0, 3, 8, 15][maxSHBands];
    const memberNames = [
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
        'rot_0', 'rot_1', 'rot_2', 'rot_3',
        ...shNames.slice(0, shCoeffs * 3)
    ];

    // Create SingleSplat for data extraction
    const singleSplat = new SingleSplat(memberNames, settings);

    // Create filter
    const filter = new GaussianFilter(settings);

    // Count total gaussians to export
    let totalCount = 0;
    for (const splat of splats) {
        filter.set(splat);
        for (let i = 0; i < splat.splatData.numSplats; ++i) {
            if (filter.test(i)) {
                totalCount++;
            }
        }
    }

    if (totalCount === 0) {
        throw new Error('No gaussians to export');
    }

    // Create DataTable columns. Tag the table with Transform.PLY because
    // SingleSplat.read pre-applies the PLY-style 180° Z flip (see
    // SplatTransformCache.getMat), so the column data is in PLY space.
    // Without this, splat-transform writers (writeSog, writeHtml) would apply
    // the flip a second time and produce upside-down output.
    const columns = memberNames.map(name => new Column(name, new Float32Array(totalCount)));
    const dataTable = new DataTable(columns, Transform.PLY);

    // Extract data into DataTable
    let idx = 0;
    for (const splat of splats) {
        filter.set(splat);
        for (let i = 0; i < splat.splatData.numSplats; ++i) {
            if (!filter.test(i)) continue;

            singleSplat.read(splat, i);

            for (let j = 0; j < memberNames.length; ++j) {
                (columns[j].data as Float32Array)[idx] = singleSplat.data[memberNames[j]] ?? 0;
            }
            idx++;
        }
    }
    return dataTable;
};

// Bridge splat-transform progress events to ReSplat's events.
const createProgressRenderer = (header: string, events?: Events): Renderer => ({
    handle: (event: LogEvent) => {
        switch (event.kind) {
            case 'scopeStart':
                if (event.depth === 0) {
                    events?.fire('progressStart', header);
                } else {
                    events?.fire('progressUpdate', {
                        text: event.index !== undefined && event.total !== undefined ?
                            `Step ${event.index} of ${event.total}: ${event.name}` :
                            event.name,
                        progress: 0
                    });
                }
                break;
            case 'scopeEnd':
                if (event.depth === 0) {
                    events?.fire('progressEnd');
                }
                break;
            case 'barStart':
                events?.fire('progressUpdate', { text: event.name, progress: 0 });
                break;
            case 'barTick':
                events?.fire('progressUpdate', {
                    progress: event.total > 0 ? 100 * event.current / event.total : 0
                });
                break;
            case 'barEnd':
                events?.fire('progressUpdate', { progress: 100 });
                break;
            case 'message':
                if (event.level === 'error') console.error(event.text);
                else if (event.level === 'warn') console.warn(event.text);
                else if (event.level === 'info') console.info(event.text);
                else if (event.level === 'debug') console.debug(event.text);
                break;
            case 'output':
                console.log(event.text);
                break;
        }
    }
});

// Marker the bundled viewer uses to fetch the embedded scene. The streamed
// export writes its base64 payload between DATA_URL_PREFIX and the closing
// quote+paren that follows it.
const DATA_URL_PREFIX = 'fetch("data:application/octet-stream;base64,';

// Base64 sub-chunk size (bytes). Must be a multiple of 3 so intermediate
// btoa calls emit no '=' padding, and small enough to stay under the
// Function.apply argument limit of String.fromCharCode.
const BASE64_CHUNK = 24 * 1024;

// Writer that base64-encodes its byte stream on the fly and forwards the
// encoded chunks to a target writer. Incoming chunk sizes are arbitrary
// (zip entries streamed by writeSog), so 0-2 remainder bytes are buffered
// between writes to preserve 3-byte group alignment — the concatenated
// output is byte-identical to whole-buffer btoa.
class Base64Writer implements Writer {
    target: Writer;
    sourceBytes = 0;
    private encoder = new TextEncoder();
    private remainder = new Uint8Array(3);
    private remainderLength = 0;
    // serialize encodes: the remainder must not be read by the next write
    // before the previous encode updated it (encode awaits target writes)
    private queue: Promise<void> = Promise.resolve();

    constructor(target: Writer) {
        this.target = target;
    }

    get bytesWritten(): number {
        return this.sourceBytes;
    }

    private encode(data: Uint8Array, flush: boolean): Promise<void> {
        this.queue = this.queue.then(async () => {
            let bytes = data;
            if (this.remainderLength > 0) {
                bytes = new Uint8Array(this.remainderLength + data.byteLength);
                bytes.set(this.remainder.subarray(0, this.remainderLength), 0);
                bytes.set(data, this.remainderLength);
                this.remainderLength = 0;
            }

            // bytes not forming a full 3-byte group carry over to the next
            // write (or are padded by btoa at close)
            const aligned = flush ? bytes.byteLength : Math.floor(bytes.byteLength / 3) * 3;
            for (let i = 0; i < aligned; i += BASE64_CHUNK) {
                const chunk = bytes.subarray(i, Math.min(i + BASE64_CHUNK, aligned));
                await this.target.write(this.encoder.encode(btoa(String.fromCharCode(...chunk))));
            }
            if (!flush) {
                this.remainder.set(bytes.subarray(aligned), 0);
                this.remainderLength = bytes.byteLength - aligned;
            }
        });
        return this.queue;
    }

    write(data: Uint8Array): Promise<void> {
        this.sourceBytes += data.byteLength;
        return this.encode(data, false);
    }

    // Flushes the buffered tail (with base64 padding). Does NOT close the
    // target writer — the HTML suffix is appended after the sog stream.
    close(): Promise<void> {
        return this.encode(new Uint8Array(0), true);
    }

    async abort(): Promise<void> {
        this.queue = Promise.resolve();
        await this.target.abort();
    }
}

// Render the bundled-viewer HTML shell by exporting a 1-row stub scene
// through writeHtml. The stub's embedded data URL is tiny, so the returned
// prefix/suffix surround the placeholder where the real (arbitrarily
// large) base64 payload is streamed. Building the shell via the library
// keeps it in sync with the installed splat-transform version instead of
// hardcoding a template copy here.
const buildViewerHtmlShell = async (viewerSettings: ExperienceSettings): Promise<{ prefix: Uint8Array; suffix: Uint8Array }> => {
    const memberNames = [
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
        'rot_0', 'rot_1', 'rot_2', 'rot_3'
    ];
    const dataTable = new DataTable(memberNames.map(name => new Column(name, new Float32Array([0]))), Transform.PLY);
    const memFs = new MemoryFileSystem();
    await writeHtml({
        filename: 'shell.html',
        dataTable,
        viewerSettingsJson: viewerSettings,
        bundle: true,
        iterations: 0,
        createDevice: createGpuDevice
    }, memFs);

    const shell = new TextDecoder().decode(memFs.results.get('shell.html') ?? new Uint8Array(0));
    const dataStart = shell.indexOf(DATA_URL_PREFIX);
    const dataEnd = dataStart !== -1 ? shell.indexOf('")', dataStart + DATA_URL_PREFIX.length) : -1;
    if (dataStart === -1 || dataEnd === -1) {
        throw new Error('Failed to locate embedded data URL in viewer HTML shell');
    }

    const encoder = new TextEncoder();
    return {
        prefix: encoder.encode(shell.slice(0, dataStart + DATA_URL_PREFIX.length)),
        suffix: encoder.encode(shell.slice(dataEnd))
    };
};

// Bundled-HTML export, streamed. splat-transform's bundled writeHtml
// materializes the whole sog, its base64 string and the final HTML string
// in memory (≈4x the sog size on top of the scene — OOM for large scenes).
// Instead the sog zip byte stream is piped through Base64Writer straight
// into the HTML output writer — the same chunked-streaming approach the
// PLY writer uses. Peak extra memory is one base64 chunk (~32KB).
const serializeViewerHtml = async (dataTable: DataTable, viewerSettings: ExperienceSettings, fs: FileSystem): Promise<void> => {
    const { prefix, suffix } = await buildViewerHtmlShell(viewerSettings);

    const htmlWriter = await fs.createWriter('output.html');
    try {
        await htmlWriter.write(prefix);

        // writeSog streams its zip output through the writer returned by
        // fs.createWriter — this tee filesystem routes those bytes through
        // Base64Writer into the HTML between prefix and suffix.
        const base64Writer = new Base64Writer(htmlWriter);
        const sogFs: FileSystem = {
            createWriter: () => base64Writer,
            mkdir: () => Promise.resolve()
        };

        await writeSogInternal({
            filename: 'temp.sog',
            dataTable,
            bundle: true,
            iterations: 0,
            createDevice: createGpuDevice
        }, sogFs);
        await htmlWriter.write(suffix);
    } finally {
        await htmlWriter.close();
    }
};

const serializeViewer = async (splats: Splat[], serializeSettings: SerializeSettings, options: ViewerExportSettings, fs: FileSystem): Promise<void> => {
    const { experienceSettings, events } = options;

    configureSplatTransform();
    splatTransformLogger.setRenderer(createProgressRenderer('Exporting HTML', events));

    // Extract splat data to DataTable
    const dataTable = extractDataTable(splats, serializeSettings);

    // splat-transform's writers leave their top-level scope open on error
    // (their contract is for the caller to unwind), so we explicitly
    // unwind here to deliver a matching depth-0 `scopeEnd(failed)` to the
    // renderer. That fires `progressEnd` and dismisses the dialog before
    // any error popup is shown.
    try {
        if (options.type === 'html') {
            // Bundled HTML — streamed assembly (memory-safe for large scenes)
            await serializeViewerHtml(dataTable, experienceSettings, fs);
        } else {
            // Package - use unbundled mode into a MemoryFileSystem, then ZIP
            const memFs = new MemoryFileSystem();
            await writeHtml({
                filename: 'index.html',
                dataTable,
                viewerSettingsJson: experienceSettings,
                bundle: false,
                iterations: 0,
                createDevice: createGpuDevice
            }, memFs);

            // Create ZIP from memory filesystem results. The try/finally
            // ensures zipFs (and its underlying writer) is closed even if a
            // write throws partway through, so we don't leak the output file.
            const zipWriter = await fs.createWriter('output.zip');
            const zipFs = new ZipFileSystem(zipWriter);
            try {
                for (const [filename, data] of memFs.results.entries()) {
                    const writer = await zipFs.createWriter(filename);
                    await writer.write(data);
                    await writer.close();
                }
            } finally {
                await zipFs.close();
            }
        }
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    }
};

// SOG serialization using splat-transform library

type SogSettings = SerializeSettings & {
    iterations: number;
    events?: Events;
    filename?: string;
};

// LCC2 (Lixel CyberColor 2) directory-format export options. Phase 1 exercises
// a single LOD with splatType='.sog'; lodLevels>1 and splatType='.spz' are
// reserved for Phase 2 (LOD auto-generation) and treated as 1/'.sog' here.
type Lcc2ExportOptions = {
    name: string;                          // scene/file base name (no extension)
    splatIdx?: number[] | null;            // selected splat indices into the splats array
    serializeSettings: SogSettings;        // reuse serializeSog's settings (maxSHBands, iterations, events, ...)
    lodLevels?: number;                    // default 1 (Phase 1; >1 reserved for Phase 2, treated as 1)
    splatType?: '.sog' | '.spz';           // default '.sog' (Phase 1 only '.sog' is exercised)
    simplifyMethod?: 'nanogs' | 'uniform'; // default 'uniform' (browser main-thread safety; backend defaults 'nanogs')
};

const serializeSog = async (splats: Splat[], settings: SogSettings, fs: FileSystem): Promise<void> => {
    const { iterations = 10, events, filename = 'output.sog' } = settings;

    configureSplatTransform();
    splatTransformLogger.setRenderer(createProgressRenderer('Exporting SOG', events));

    // Extract splat data to DataTable
    const dataTable = extractDataTable(splats, settings);

    // splat-transform's writers leave their top-level scope open on error
    // (their contract is for the caller to unwind), so we explicitly
    // unwind here to deliver a matching depth-0 `scopeEnd(failed)` to the
    // renderer. That fires `progressEnd` and dismisses the dialog before
    // any error popup is shown.
    try {
        await writeSogInternal({
            filename,
            dataTable,
            bundle: true,
            iterations,
            createDevice: createGpuDevice
        }, fs);
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    }
};

// Apply the PLY→LCC2 coordinate conversion in-place on a PLY-space DataTable.
//
// writeSog always encodes in PLY space (bakeTransform → Z(180) rotation).
// readLcc2Source applies LCC2_TRANSFORM = fromEulers(90, 0, 180) on top of
// the PLY-space SOG data. Without compensation the import round-trip would
// accumulate an extraneous Rx(-90°) rotation. The spatial chain
//   p_lcc2 = Rz(180°) * Rx(90°) * Rz(180°) * p_ply
// simplifies to Rx(-90°) = (x, z, -y). Positions, rotation quaternions, and
// SH coefficients (all degrees) receive the same Rx(-90°) transform so each
// gaussian's orientation, shape, and view-dependent lighting stay consistent.
//
//   Position:     (x, y, z) → (x, z, -y).
//   Quaternion:   q' = Rx(-90°) * q  (left-multiply, w-first order).
//   SH l=1:       (y→z, z→-y, x unchanged) per channel (manual, kept as-is).
//   SH l≥2:       rotated via SHRotation. Previously these were left
//                 uncompensated — view-dependent lighting then disagreed with
//                 the splats' orientation and some blocks rendered black from
//                 certain angles.
//
// Returns the scene AABB in LCC2 space (splat-center extents, not
// scale-expanded).
const applyLcc2CoordinateTransform = (dataTable: DataTable, maxSHBands: number): { min: [number, number, number]; max: [number, number, number] } => {
    const N = dataTable.numRows;
    const xs = dataTable.getColumnByName('x')!.data as Float32Array;
    const ys = dataTable.getColumnByName('y')!.data as Float32Array;
    const zs = dataTable.getColumnByName('z')!.data as Float32Array;
    const r0 = dataTable.getColumnByName('rot_0')!.data as Float32Array;
    const r1 = dataTable.getColumnByName('rot_1')!.data as Float32Array;
    const r2 = dataTable.getColumnByName('rot_2')!.data as Float32Array;
    const r3 = dataTable.getColumnByName('rot_3')!.data as Float32Array;
    const SQRT1_2 = Math.SQRT1_2;

    // Degree-1..3 SH rotation. getColumnByName returns Column|null, so test for
    // null (not undefined) — an earlier version used `!== undefined` which is
    // always true for a Column|null return and would crash on SH-less scenes.
    const shCoeffs = [0, 3, 8, 15][maxSHBands];
    let shCols: Float32Array[] | null = null;
    let shRot: SHRotation | null = null;
    const shTmp = new Float32Array(15);
    if (shCoeffs >= 3 && dataTable.getColumnByName('f_rest_0')) {
        shCols = [];
        for (let ch = 0; ch < 3; ++ch) {
            const base = ch * shCoeffs;
            for (let d = 0; d < shCoeffs; ++d) {
                shCols.push(dataTable.getColumnByName(`f_rest_${base + d}`)!.data as Float32Array);
            }
        }
        if (shCoeffs >= 8) {
            // Rx(-90°) in column-major Mat3 layout: (x, y, z) → (x, z, -y).
            const rotMat = new Mat3();
            rotMat.data.set([1, 0, 0, 0, 0, -1, 0, 1, 0]);
            shRot = new SHRotation(rotMat);
        }
    }

    for (let i = 0; i < N; ++i) {
        const x = xs[i];
        const y = ys[i];
        const z = zs[i];
        xs[i] = x;
        ys[i] = z;
        zs[i] = -y;

        // q' = Rx(-90°) * q  (left-multiply, w-first order)
        const qw = r0[i], qx = r1[i], qy = r2[i], qz = r3[i];
        r0[i] = (qw + qx) * SQRT1_2;
        r1[i] = (qx - qw) * SQRT1_2;
        r2[i] = (qy + qz) * SQRT1_2;
        r3[i] = (qz - qy) * SQRT1_2;

        // SH l=1: rotate per-channel (y→z, z→-y, x unchanged) — existing
        // compensation kept as-is.
        if (shCols) {
            for (let ch = 0; ch < 3; ++ch) {
                const b = ch * shCoeffs;
                const v0 = shCols[b + 0][i], v1 = shCols[b + 1][i];
                shCols[b + 0][i] = v1;
                shCols[b + 1][i] = -v0;
            }
        }
        // SH l>=2: rotate to match the coordinate transform. Previously left
        // uncompensated — view-dependent lighting then disagreed with splat
        // orientation and some blocks rendered black from certain angles.
        if (shCols && shRot) {
            for (let ch = 0; ch < 3; ++ch) {
                const b = ch * shCoeffs;
                for (let d = 0; d < shCoeffs; ++d) shTmp[d] = shCols[b + d][i];
                shRot.apply(shTmp);
                for (let d = 2; d < shCoeffs; ++d) shCols[b + d][i] = shTmp[d];
            }
        }
    }

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < N; ++i) {
        const x = xs[i], y = ys[i], z = zs[i];
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (z < minZ) minZ = z;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
        if (z > maxZ) maxZ = z;
    }
    return { min: [minX, minY, minZ], max: [maxX, maxY, maxZ] };
};

// LCC2 node metadata shape (matches the v0.0.3 schema).
type Lcc2NodeRef = { name: number; start: number; count: number };
type Lcc2Aabb = { min: [number, number, number]; max: [number, number, number] };

// Adaptive hybrid tree node. Built once on the finest splats and walked
// per-depth to emit LOD-sampled chunks. Mirrors the reference LCC2 topology
// (英华楼.lcc2: root has 21 spatial-cell children, ~234 LOD-chain nodes,
// ~95 spatial-split nodes, node count grows linearly 21→21→24→33→49→67→113→182
// across 8 depths — NOT exponentially).
//
//   child === null       → leaf (depth === treeDepth, finest LOD level)
//   child.length === 1   → LOD chain (same AABB as parent; next-finer LOD
//                          level in the SAME spatial cell). Dominant case.
//   child.length > 1     → spatial split (each child has a smaller, tight
//                          AABB; happens only when a cell's finest count
//                          exceeds sogChunkTarget so it must subdivide for
//                          streaming granularity).
//
// `finestIndices` holds ALL finest-level splat indices belonging to this
// spatial cell (NOT LOD-sampled). LOD chains share the parent's array by
// reference (zero copy); spatial splits allocate subset arrays (sum of
// children lengths = parent length). Per-depth LOD subsets are extracted on
// demand during chunk writing via interval sampling at step 2^(treeDepth-D).
type AdaptiveNode = {
    aabb: Lcc2Aabb;
    finestIndices: Uint32Array;
    child: AdaptiveNode[] | null;
    id?: string;
};

// Compute a tight AABB around the given splat indices. Used at split time so
// each child cell gets a minimal (data-fitted) bounding box rather than the
// theoretical octant box — matches the reference structure where parent and
// LOD-chain child share an identical tight AABB.
const computeTightAabb = (
    indices: Uint32Array,
    xs: Float32Array, ys: Float32Array, zs: Float32Array
): Lcc2Aabb => {
    let mn0 = Infinity, mn1 = Infinity, mn2 = Infinity;
    let mx0 = -Infinity, mx1 = -Infinity, mx2 = -Infinity;
    for (let i = 0; i < indices.length; ++i) {
        const idx = indices[i];
        const x = xs[idx], y = ys[idx], z = zs[idx];
        if (x < mn0) mn0 = x;
        if (y < mn1) mn1 = y;
        if (z < mn2) mn2 = z;
        if (x > mx0) mx0 = x;
        if (y > mx1) mx1 = y;
        if (z > mx2) mx2 = z;
    }
    return { min: [mn0, mn1, mn2], max: [mx0, mx1, mx2] };
};

// Build an adaptive hybrid tree on the finest splats.
//
// At each depth D (1..treeDepth) a node either:
//   - chains: single child with the SAME aabb (LOD link to the next-finer
//     level in the same spatial cell), OR
//   - splits: octree 8-way subdivision (empty octants dropped), each child
//     getting a tight data-fitted AABB.
//
// Split decision: subdivide when the cell's finest splat count exceeds
// `sogChunkTarget`. This keeps per-cell finest data streamable in one chunk
// and produces linear node growth (~cells × treeDepth) instead of the
// exponential 2^(L+2) growth of the old fixed binary tree — which is what
// froze the UE plugin for lodLevels > 6.
//
// The octree branch factor (max 8 children per split, empty octants dropped)
// matches the reference's variable childNum distribution (2..5 observed; 8
// max). Root childNum is data-dependent (8..64 typically), giving the UE
// plugin enough spatial cells for streaming without an explosion of nodes.
//
// Memory: LOD chains share `finestIndices` by reference (zero copy). Only
// spatial splits allocate new Uint32Arrays, and their lengths sum to the
// parent's. Total allocation ≈ N × (number of split depths), typically 1-3
// levels → 1N-3N, far below the 4GB browser limit.
//
// `maxSplitDepth` caps recursion for degenerate inputs (e.g. all splats at
// one point) where octree subdivision never reduces cell count below target.
const buildAdaptiveLcc2Tree = (
    xs: Float32Array, ys: Float32Array, zs: Float32Array,
    N: number, treeDepth: number, sceneAabb: Lcc2Aabb,
    splitTarget: number,
    maxSplitDepth = 8
): AdaptiveNode => {
    const rootIndices = new Uint32Array(N);
    for (let i = 0; i < N; ++i) rootIndices[i] = i;

    // Recursively build children for a cell holding `indices` at `depth`.
    // Returns null past leaf depth, otherwise 1 (chain) or 2..8 (split) children.
    // depth ranges 1..treeDepth inclusive: depth=treeDepth is the finest level
    // (k=0), so we must NOT stop at depth===treeDepth — stopping there would
    // drop the finest LOD and leave the UE plugin with no k=0 data (freeze).
    const buildChildren = (
        indices: Uint32Array, aabb: Lcc2Aabb, depth: number, splitDepth: number
    ): AdaptiveNode[] | null => {
        if (depth > treeDepth) return null;

        // LOD chain when the cell is small enough OR we've exhausted split budget.
        if (indices.length <= splitTarget || splitDepth >= maxSplitDepth) {
            const child: AdaptiveNode = { aabb, finestIndices: indices, child: null };
            child.child = buildChildren(indices, aabb, depth + 1, splitDepth);
            return [child];
        }

        // Spatial split: octree 8-way at the AABB midpoints, drop empty octants.
        const [mn0, mn1, mn2] = aabb.min;
        const [mx0, mx1, mx2] = aabb.max;
        const mid0 = (mn0 + mx0) * 0.5;
        const mid1 = (mn1 + mx1) * 0.5;
        const mid2 = (mn2 + mx2) * 0.5;

        const bucketCounts = new Uint32Array(8);
        for (let i = 0; i < indices.length; ++i) {
            const idx = indices[i];
            const bX = xs[idx] <= mid0 ? 0 : 1;
            const bY = ys[idx] <= mid1 ? 0 : 1;
            const bZ = zs[idx] <= mid2 ? 0 : 1;
            bucketCounts[(bX << 2) | (bY << 1) | bZ]++;
        }

        // Pre-allocate per-octant index arrays.
        const bucketArrays: (Uint32Array | null)[] = new Array(8).fill(null);
        const bucketFill = new Uint32Array(8);
        for (let o = 0; o < 8; ++o) {
            if (bucketCounts[o] > 0) bucketArrays[o] = new Uint32Array(bucketCounts[o]);
        }
        for (let i = 0; i < indices.length; ++i) {
            const idx = indices[i];
            const bX = xs[idx] <= mid0 ? 0 : 1;
            const bY = ys[idx] <= mid1 ? 0 : 1;
            const bZ = zs[idx] <= mid2 ? 0 : 1;
            const o = (bX << 2) | (bY << 1) | bZ;
            bucketArrays[o]![bucketFill[o]++] = idx;
        }

        const children: AdaptiveNode[] = [];
        for (let o = 0; o < 8; ++o) {
            if (bucketCounts[o] === 0) continue; // drop empty octant
            const childIndices = bucketArrays[o]!;
            // Tighten AABB to actual splat bounds (smaller than the octant box).
            const childAabb = computeTightAabb(childIndices, xs, ys, zs);
            const child: AdaptiveNode = { aabb: childAabb, finestIndices: childIndices, child: null };
            child.child = buildChildren(childIndices, childAabb, depth + 1, splitDepth + 1);
            children.push(child);
        }
        // Guard: if all splats landed in one octant (degenerate data), fall back
        // to a chain to avoid infinite recursion.
        if (children.length === 1) {
            // Already built — but force chain semantics by stopping further splits.
            const only = children[0];
            only.child = buildChildren(only.finestIndices, only.aabb, depth + 1, maxSplitDepth);
        }
        return children;
    };

    const root: AdaptiveNode = { aabb: sceneAabb, finestIndices: rootIndices, child: null };
    root.child = buildChildren(rootIndices, sceneAabb, 1, 0);
    return root;
};

// Assign node IDs in the XGRIDS path convention: "0", "0_0", "0_0_1", ...
// Called once after tree construction, before emitAdaptiveTreeJson or any
// file naming that depends on node IDs. The child array index determines the
// serial number in the ID.
const assignAdaptiveNodeIds = (root: AdaptiveNode): void => {
    const walk = (node: AdaptiveNode, id: string) => {
        node.id = id;
        if (node.child) {
            for (let i = 0; i < node.child.length; ++i) {
                walk(node.child[i], `${id}_${i}`);
            }
        }
    };
    walk(root, '0');
};

// Collect all tree nodes at exactly `targetDepth` (root is depth 0; root's
// children are depth 1). Used by the per-depth chunk writer to iterate every
// spatial cell that contributes data to LOD level (treeDepth - targetDepth).
const collectNodesAtDepth = (root: AdaptiveNode, targetDepth: number): AdaptiveNode[] => {
    const result: AdaptiveNode[] = [];
    const walk = (node: AdaptiveNode, depth: number) => {
        if (depth === targetDepth) {
            result.push(node);
            return;
        }
        if (node.child) {
            for (const c of node.child) walk(c, depth + 1);
        }
    };
    if (root.child) {
        for (const c of root.child) walk(c, 1);
    }
    return result;
};

// Backfill depth-D nodes that received no splats from a coarser source LOD.
// A source LOD's splat distribution does not always align with the adaptive
// tree built on the finest LOD, leaving some cells empty. The LCC2 reader
// (collectChunksByLevel) requires (a) every node to carry `data['3dgs']` and
// (b) each chunk's node counts to sum exactly to the chunk's decoded splat
// count. The old "borrow the first child's file" fallback cross-referenced a
// finer level's chunk file and broke (b), so we instead duplicate the nearest
// current-LOD splat into each empty node. This keeps the count invariant while
// giving the node a representative splat for ComputeError.
const backfillEmptyLodNodes = (
    nodesAtD: AdaptiveNode[],
    counts: number[],
    nodeSubsets: (Uint32Array | null)[],
    lodXs: Float32Array,
    lodYs: Float32Array,
    lodZs: Float32Array,
    lodN: number
): number => {
    let added = 0;
    for (let i = 0; i < nodesAtD.length; ++i) {
        if (counts[i] > 0) continue;
        const [mn0, mn1, mn2] = nodesAtD[i].aabb.min;
        const [mx0, mx1, mx2] = nodesAtD[i].aabb.max;
        const cx = (mn0 + mx0) * 0.5;
        const cy = (mn1 + mx1) * 0.5;
        const cz = (mn2 + mx2) * 0.5;
        let best = -1;
        let bestD = Infinity;
        for (let j = 0; j < lodN; ++j) {
            const dx = lodXs[j] - cx;
            const dy = lodYs[j] - cy;
            const dz = lodZs[j] - cz;
            const d = dx * dx + dy * dy + dz * dz;
            if (d < bestD) {
                bestD = d;
                best = j;
            }
        }
        if (best >= 0) {
            nodeSubsets[i] = new Uint32Array([best]);
            counts[i] = 1;
            added++;
        }
    }
    return added;
};

// Descend the adaptive tree to find the depth-`targetDepth` node whose AABB
// contains the point (x,y,z). Used by serializeLcc2FromLodLog to bin coarser-
// LOD splats (loaded from file) into the same tree that was built on the
// finest LOD.
//
// Descent rules:
//   - At the root, find the depth-1 child whose AABB contains the point.
//   - At a LOD-chain node (childNum===1), follow the single child (same AABB).
//   - At a spatial-split node (childNum>1), find the child whose AABB contains
//     the point.
//   - If no child contains the point (degenerate — point on a boundary, or
//     floating-point drift), return the current node so the splat is not lost.
//
// O(targetDepth × max_children) per call. For targetDepth ≤ 20 and max 8
// children: ≤160 comparisons per splat. Coarser LODs are small (N/2^k), so
// total binning work across all coarser levels ≈ 6N comparisons (geometric
// series) — a few seconds for N = 35M.
const findAdaptiveNodeAtDepth = (
    root: AdaptiveNode,
    x: number, y: number, z: number,
    targetDepth: number
): AdaptiveNode | null => {
    if (!root.child || root.child.length === 0) return null;

    // Find the depth-1 child containing the point.
    let node: AdaptiveNode | null = null;
    for (const c of root.child) {
        const [mn0, mn1, mn2] = c.aabb.min;
        const [mx0, mx1, mx2] = c.aabb.max;
        if (x >= mn0 && x <= mx0 && y >= mn1 && y <= mx1 && z >= mn2 && z <= mx2) {
            node = c;
            break;
        }
    }
    if (!node) return null;

    for (let d = 2; d <= targetDepth; ++d) {
        if (!node.child || node.child.length === 0) return node; // leaf
        if (node.child.length === 1) {
            // LOD chain: child shares the same AABB, just follow.
            node = node.child[0];
            continue;
        }
        // Spatial split: find the child whose AABB contains the point.
        let next: AdaptiveNode | null = null;
        for (const c of node.child) {
            const [mn0, mn1, mn2] = c.aabb.min;
            const [mx0, mx1, mx2] = c.aabb.max;
            if (x >= mn0 && x <= mx0 && y >= mn1 && y <= mx1 && z >= mn2 && z <= mx2) {
                next = c;
                break;
            }
        }
        if (!next) return node; // degenerate: point on boundary or FP drift
        node = next;
    }
    return node;
};

// Emit the nested LCC2 tree JSON from the adaptive tree. Node IDs follow the
// path convention `0`, `0_0`, `0_0_0`, ... matching the reference structure.
// `nodeRefs` maps each AdaptiveNode to its {name, start, count} chunk ref
// (assigned during per-depth chunk writing). Nodes with no ref or count===0
// get data:null — but with the adaptive tree every node has finest splats, so
// every depth has data. Returns the root JSON object (without splatFiles/
// meshFiles/bvhFiles, which the caller attaches).
const emitAdaptiveTreeJson = (
    root: AdaptiveNode,
    nodeRefs: Map<AdaptiveNode, Lcc2NodeRef>,
    splatFiles: string[]
): any => {
    const emit = (node: AdaptiveNode): any => {
        const ref = nodeRefs.get(node);
        let data = ref && ref.count > 0 ? { '3dgs': ref } : null;
        if (!node.child || node.child.length === 0) {
            return { id: node.id, boundingBox: node.aabb, childNum: 0, data };
        }
        const child: Record<string, any> = {};
        for (let i = 0; i < node.child.length; ++i) {
            child[String(i)] = emit(node.child[i]);
        }
        // Chain nodes (childNum=1, same AABB) with 0 own splats at their LOD
        // level get a minimal 1-splat data reference from the first child.
        // Degenerate cells (zero-volume AABB from coincident splats) can yield
        // count=0 at coarser LODs; the UE plugin's LCC2Tree::ComputeError
        // dereferences node.data.3dgs unconditionally, so null data →
        // EXCEPTION_ACCESS_VIOLATION. We assign count=1 (not full inheritance)
        // because full inheritance would make the UE plugin render the finer
        // LOD's entire splat count for this cell — up to 2.8x more splats per
        // frame (~20fps drop). 1 representative splat is enough for ComputeError
        // and keeps render cost minimal; the plugin descends to finer LODs
        // (showing full detail) when the camera is close. Children are emitted
        // above, so child['0'].data is already resolved (cascades through
        // multi-level chains).
        if (!data && child['0'] && child['0'].data && child['0'].data['3dgs']) {
            const cd = child['0'].data['3dgs'] as { name: number; start: number; count: number };
            data = { '3dgs': { name: cd.name, start: cd.start, count: 1 } };
        }
        return { id: node.id, boundingBox: node.aabb, childNum: node.child.length, data, child };
    };

    const child: Record<string, any> = {};
    if (root.child) {
        for (let i = 0; i < root.child.length; ++i) {
            child[String(i)] = emit(root.child[i]);
        }
    }
    return {
        id: root.id,
        boundingBox: root.aabb,
        childNum: root.child?.length ?? 0,
        data: null,
        splatFiles,
        meshFiles: [] as string[],
        bvhFiles: [] as string[],
        child
    };
};

// LCC2 (Lixel CyberColor 2) directory-format serialization. Phase 1 writes a
// single LOD; Phase 2 (lodLevels>1) builds a multi-level LOD tree.
//
// Phase 2 LOD generation (adaptive hybrid tree):
//   - An adaptive tree of depth L is built on the finest splats. Each node
//     either chains (childNum=1, same AABB → LOD link to next-finer level in
//     the same spatial cell) or splits (octree 8-way, empty octants dropped →
//     spatial subdivision for streaming granularity). See buildAdaptiveLcc2Tree.
//   - LOD level k (0=finest..L-1=coarsest) samples each cell's finestIndices
//     at rate = 1 - 0.9*k/(L-1): LOD0=100%, LOD(L-1)=10%, linear in between.
//     This guarantees strictly decreasing fidelity per level (no duplicates),
//     with the coarsest level always holding 10% — unlike the old step=2^k
//     scheme which collapsed to 100/50/25/12.5… and starved coarse LODs.
//   - All depth-D nodes' samples are concatenated into one or more .sog chunks
//     (≤ SOG_CHUNK_TARGET splats each); each node's {name, start, count} points
//     into its level's chunk(s).
//   - The finest level (k=0, rate=100%) is written directly from the finest
//     DataTable with `indices` = the node-ordered full permutation (no clone;
//     writeSog skips its internal Morton sort and emits in our order).
//   - Coarser levels (k≥1) are written from a column-by-column subset (sized
//     to N×rate, never full-size) with identity `indices` to preserve node
//     order. Peak memory = finest + one smaller subset.
//   - splatFiles is ordered coarsest→finest (depth 1 → index 0), matching the
//     reference sample; node.data.3dgs.name = the splatFiles index of the
//     chunk holding that node's LOD-D data.
//   - Node count grows LINEARLY with L (≈ cells × L), so the UE plugin can
//     traverse high-LOD trees without freezing — unlike the old fixed binary
//     tree whose 2^(L+2) leaves froze the plugin for L > 6.

// Stream one LOD level of a multi-LOD LCC splat into a PLY-space DataTable,
// applying the splat's LodEditLog deletions and reusing its world transform
// / color grade (mirrors serializeLcc2FromLodLog's loadLodForExport). Used by
// serializeLcc2 to merge the LCC source's own LOD-k into output level k.
const loadLccLodForExport = async (lccSplat: Splat, lodIndex: number, settings: SerializeSettings): Promise<DataTable> => {
    if (!lccSplat.lccFilePath || !lccSplat.lccFileSystem || !lccSplat.lodEditLog) {
        throw new Error('[LCC2] loadLccLodForExport requires multi-LOD LCC metadata');
    }
    const rawDataTable = await loadLodDataTable(lccSplat.lccFilePath, lccSplat.lccFileSystem, lodIndex);
    const gsplatData = dataTableToGSplatData(rawDataTable);

    // Add 'state' property (all 0) for GaussianFilter's deleted checks.
    if (!gsplatData.getProp('state')) {
        gsplatData.getElement('vertex').properties.push({
            type: 'uchar',
            name: 'state',
            storage: new Uint8Array(gsplatData.numSplats),
            byteSize: 1
        });
    }
    // Apply deletions recorded in LodEditLog (world-space voxel bitmaps).
    lccSplat.lodEditLog.applyDeletionsToState(
        gsplatData.getProp('state') as Uint8Array,
        gsplatData.getProp('x') as Float32Array,
        gsplatData.getProp('y') as Float32Array,
        gsplatData.getProp('z') as Float32Array,
        lccSplat.worldTransform
    );
    if (lodIndex === 0 && lccSplat.pagedLodEditSession) {
        const state = gsplatData.getProp('state') as Uint8Array;
        for (let i = 0; i < state.length; i++) {
            if (lccSplat.pagedLodEditSession.isSourceDeleted(i)) state[i] |= State.deleted;
        }
    }

    // Mock Splat: reuse the current splat's entity/world transform and color
    // grade; a null transformTexture source → identity per-splat transform.
    const mockSplat = {
        splatData: gsplatData,
        numSplats: gsplatData.numSplats,
        transformTexture: { getSource: (): null => null },
        transformPalette: null,
        entity: lccSplat.entity,
        tintClr: lccSplat.tintClr,
        temperature: lccSplat.temperature,
        saturation: lccSplat.saturation,
        brightness: lccSplat.brightness,
        blackPoint: lccSplat.blackPoint,
        whitePoint: lccSplat.whitePoint,
        transparency: lccSplat.transparency
    } as unknown as Splat;

    return extractDataTable([mockSplat], settings);
};

// Concatenate DataTables with identical column layout (same names/order) into
// one table. Used to merge non-LCC rows with streamed LCC LOD rows.
const concatDataTables = (tables: DataTable[]): DataTable => {
    const totalRows = tables.reduce((sum, t) => sum + t.numRows, 0);
    const cols = tables[0].columns.map((c, ci) => {
        const data = new Float32Array(totalRows);
        let off = 0;
        for (const t of tables) {
            const src = t.columns[ci].data as Float32Array;
            data.set(src, off);
            off += src.length;
        }
        return new Column(c.name, data);
    });
    return new DataTable(cols, tables[0].transform);
};

const serializeLcc2 = async (splats: Splat[], options: Lcc2ExportOptions, fs: FileSystem): Promise<void> => {
    const { name, serializeSettings, splatIdx } = options;
    // Default >0: splat-transform's shN k-means skips its Lloyd loop at 0
    // iterations, quantizing every splat to the same SH centroid and breaking
    // view-dependent lighting (blocks render black/white).
    const { iterations = 10, events } = serializeSettings;
    const splatType = options.splatType ?? '.sog';
    // Browser path defaults to 'uniform' for main-thread safety: NanoGS runs
    // pure-TS KNN+cost on the main thread and can jank the UI for large nodes.
    // Users opt in via the export popup. The backend (server/lcc2-export.mjs)
    // defaults to 'nanogs' since it runs in a worker_thread off the main loop.
    const simplifyMethod = options.simplifyMethod ?? 'uniform';
    // Clamp lodLevels to [1,20]. Max 20 matches the UE plugin's EndLevel cap
    // (LCCMacro.h). The adaptive hybrid tree keeps node count linear in L, so
    // values up to 20 are safe — unlike the old binary tree which froze the
    // plugin past 6.
    let lodLevels = Math.max(1, Math.min(20, options.lodLevels ?? 6));

    const selectedSplats = splatIdx?.length ? splatIdx.map(i => splats[i]) : splats;

    await fs.mkdir(`${name}/data/3dgs`);

    configureSplatTransform();

    // Custom progress renderer: suppresses progressStart/progressEnd (dialog
    // lifecycle managed manually) and shows "正在导出 LOD{N}" instead of raw
    // splat-transform step names like "k-means". Sub-step bar progress is
    // suppressed to prevent resetting the cumulative percentage.
    let currentExportLod = -1;
    let lodExportProgress = 0;
    splatTransformLogger.setRenderer({
        handle: (event: LogEvent) => {
            switch (event.kind) {
                case 'scopeStart':
                    if (event.depth > 0) {
                        const label = currentExportLod >= 0 ? `正在导出 LOD${currentExportLod}` : event.name;
                        events?.fire('progressUpdate', { text: `${label}  —  ${lodExportProgress}%` });
                    }
                    break;
                case 'scopeEnd':
                    break;
                case 'barStart':
                case 'barTick':
                case 'barEnd':
                    break;
                case 'message':
                    if (event.level === 'error') console.error(event.text);
                    else if (event.level === 'warn') console.warn(event.text);
                    break;
            }
        }
    });

    events?.fire('progressStart', 'Exporting LCC2');

    // Cap maxSHBands to the source splats' actual SH bands. extractDataTable
    // allocates Float32Array(totalCount) per output column; if the user requests
    // SH=3 (59 cols) but the source is SH=0 (14 cols), the 45 f_rest columns
    // are zero-filled — wasting N×45×4 bytes (e.g. 35.7M splats → 3.2 GB) and
    // pushing peak memory past the ~4 GB renderer limit ("Array buffer
    // allocation failed"). Mirrors serializePlyCompressed's outputSHBands logic.
    const requestedSHBands = serializeSettings.maxSHBands ?? 3;
    const sourceMaxSHBands = Math.max(...selectedSplats.map(s => calcSHBands(getVertexProperties(s.splatData))));
    const maxSHBands = Math.min(requestedSHBands, sourceMaxSHBands);
    if (maxSHBands < requestedSHBands) {
        console.warn(
            `[LCC2] Source SH=${sourceMaxSHBands} < requested SH=${requestedSHBands}; ` +
            `capping to SH=${maxSHBands} (avoids zero-fill column OOM)`
        );
    }
    const exportSettings: SerializeSettings = { ...serializeSettings, maxSHBands };

    // Build the splat-transform DataTable ONCE (PLY space). SingleSplat.read
    // pre-applies the PLY-style 180° Z flip + world transform.
    //
    // Mixed-scene handling: multi-LOD LCC splats contribute their OWN LOD
    // levels to the export (each output level k merges the other splats'
    // simplified data with the LCC source's LOD-k), so their rows here come
    // from the LCC source's FINEST LOD (streamed, independent of the
    // currently loaded LOD) and are concatenated after the other splats'
    // rows.
    const lccPreserveSplats = selectedSplats.filter(s => !!s.lccFilePath && s.lodCounts.length >= 2 && !!s.lodEditLog);
    const otherSplats = selectedSplats.filter(s => lccPreserveSplats.indexOf(s) === -1);
    const hasLccRows = lccPreserveSplats.length > 0;

    let nlN = 0;
    let dataTable: DataTable;
    if (hasLccRows) {
        const tables: DataTable[] = [];
        if (otherSplats.length > 0) {
            tables.push(extractDataTable(otherSplats, exportSettings));
            nlN = tables[0].numRows;
        }
        for (const lccSplat of lccPreserveSplats) {
            tables.push(await loadLccLodForExport(lccSplat, 0, exportSettings));
        }
        dataTable = concatDataTables(tables);
    } else {
        dataTable = extractDataTable(selectedSplats, exportSettings);
        nlN = dataTable.numRows;
    }
    const N = dataTable.numRows;
    const lccTotal = N - nlN;

    // Convert PLY-space data to LCC2-native space (in-place). Done before any
    // clone so coarser-level subsets inherit the transform.
    const boundingBox = applyLcc2CoordinateTransform(dataTable, maxSHBands);

    const fileType = maxSHBands > 0 ? 'quality' : 'portable';
    const guid = crypto.randomUUID().replace(/-/g, '');

    // splatFiles[k-file-index] : depth D -> index D-1 (coarsest first).
    // chunkPaths[k] : level k (0=finest). splatFiles[d] = chunkPaths[L-1-d].
    const splatFiles: string[] = [];
    const lodSplats: number[] = [];
    let root: any;

    try {
        // Auto-select tree depth (LOD levels).
        //   - safeTreeDepth: minimum for chunk safety (per-depth total ≤ SOG_CHUNK_TARGET)
        //   - User slider value is honored; only raised when needed for chunk safety.
        //   - Per-depth merging means small nodes don't cause file explosion.
        //   - Max 20 = UE plugin's EndLevel cap (LCCMacro.h). The adaptive tree
        //     keeps node count linear in L, so values up to 20 are safe.
        const SOG_CHUNK_TARGET = 3_000_000;
        // Per-cell spatial split threshold (~500K splats). Cells below this
        // size chain (LOD link) instead of splitting. This is deliberately
        // much smaller than SOG_CHUNK_TARGET because the chunking loop
        // already packs multiple spatial cells into one .sog file (up to
        // SOG_CHUNK_TARGET). A smaller split threshold creates more spatial
        // cells → finer LOD granularity → the UE plugin renders fewer splats
        // per visible node (fewer draw calls per cell). The source XGRIDS
        // format uses ~400K max per cell; targeting ~500K matches that.
        const SPLIT_TARGET = 500_000;
        const safeTreeDepth = Math.max(1, Math.ceil(Math.log2(N / SOG_CHUNK_TARGET)) - 2);
        const treeDepth = Math.min(20, Math.max(safeTreeDepth, lodLevels));
        if (treeDepth > lodLevels) {
            console.warn(
                `[LCC2] auto-raised lodLevels ${lodLevels}→${treeDepth} ` +
                `(N=${N.toLocaleString()}, SOG_CHUNK_TARGET=${SOG_CHUNK_TARGET.toLocaleString()})`
            );
        }
        // Rough encode-time estimate.
        const shFactor = maxSHBands > 0 ? 1 : 0.25;
        const estMin = Math.max(1, Math.round((N / 3.8e6) * 10 * shFactor));
        console.warn(`[LCC2] N=${N.toLocaleString()} treeDepth=${treeDepth} SH=${maxSHBands} est ~${estMin} min`);
        lodLevels = treeDepth;

        if (treeDepth <= 1) {
            // ---- Phase 1: single LOD ----
            const lodFileName = '0_0.sog';
            const chunkPath = `${name}/data/3dgs/${lodFileName}`;

            currentExportLod = 0;
            lodExportProgress = 100;
            events?.fire('progressUpdate', { text: '正在导出 LOD0  —  0%', progress: 0 });

            await writeSogInternal({
                filename: chunkPath,
                dataTable,
                bundle: true,
                iterations,
                createDevice: createGpuDevice
            }, fs);

            events?.fire('progressUpdate', { text: `LOD 0: ${N.toLocaleString()} splats  —  100%`, progress: 100 });
            splatFiles.push(`data/3dgs/${lodFileName}`);
            lodSplats.push(N);
            root = {
                id: '0',
                boundingBox,
                childNum: 1,
                data: null,
                splatFiles,
                meshFiles: [] as string[],
                bvhFiles: [] as string[],
                child: {
                    '0': {
                        id: '0_0',
                        boundingBox,
                        childNum: 0,
                        data: { '3dgs': { name: 0, start: 0, count: N } }
                    }
                }
            };
        } else {
            // ---- Phase 2: multi-level LOD with adaptive hybrid tree ----
            //
            // The adaptive tree is built once on the finest splats
            // (buildAdaptiveLcc2Tree). Each depth D (1..treeDepth) holds one
            // LOD level; nodes at depth D are collected and their finestIndices
            // are interval-sampled at step 2^(treeDepth-D) to produce that
            // level's data. All depth-D samples are concatenated into 1..N
            // .sog chunks (≤ SOG_CHUNK_TARGET splats each); each node's
            // {name, start, count} points into its level's chunk(s).
            const xs = dataTable.getColumnByName('x')!.data as Float32Array;
            const ys = dataTable.getColumnByName('y')!.data as Float32Array;
            const zs = dataTable.getColumnByName('z')!.data as Float32Array;

            events?.fire('progressUpdate', { text: 'Building spatial tree…  —  0%', progress: 0 });
            console.warn(`[LCC2] building adaptive tree: N=${N.toLocaleString()}, treeDepth=${treeDepth}`);
            const adaptiveRoot = buildAdaptiveLcc2Tree(xs, ys, zs, N, treeDepth, boundingBox, SPLIT_TARGET);
            assignAdaptiveNodeIds(adaptiveRoot);
            const depth1Count = adaptiveRoot.child?.length ?? 0;
            // Count total nodes for logging.
            let totalNodes = 0;
            const countNodes = (n: AdaptiveNode) => {
                totalNodes++;
                if (n.child) for (const c of n.child) countNodes(c);
            };
            if (adaptiveRoot.child) for (const c of adaptiveRoot.child) countNodes(c);
            console.warn(`[LCC2] adaptive tree done: ${depth1Count} root children, ${totalNodes} total nodes`);

            // nodeRefs maps each AdaptiveNode to its {name, start, count} chunk ref.
            const nodeRefs = new Map<AdaptiveNode, Lcc2NodeRef>();
            const lodSplatsByLevel = new Array(treeDepth).fill(0);
            // Track chunk index per LOD level; k=0=finest.
            const chunkIdxByLevel = new Array(treeDepth).fill(0);
            const srcCols = dataTable.columns;
            const numCols = srcCols.length;

            // --- NanoGS per-node simplification setup (browser mirror of
            // server/lcc2-export.mjs). The browser path defaults to 'uniform'
            // for main-thread safety; NanoGS only runs when the user opts in.
            // Both paths share the same activation/deactivation glue
            // (nodeAttrsFromColumns/nodeAttrsToColumns) and simplifyNode entry
            // point, kept under CC BY-NC 4.0 (see src/nanogs/README.md). ---
            const useNanogsRequested = simplifyMethod === 'nanogs';
            // Browser safety guard (plan §4.3): NanoGS runs pure-TS KNN+cost on
            // the main thread. For large scenes the accumulated per-node work
            // (many nodes × KNN passes) janks the UI and risks OOM. Force
            // uniform above 2M splats even when the user opted in. The backend
            // worker_thread path (>10M, auto-routed in file-handler) has no
            // such guard — it runs NanoGS off the main loop.
            const NANOGS_BROWSER_MAX = 2_000_000;
            // NanoGS merges a whole node's rows — it cannot selectively merge
            // a subset, so it would alter the multi-LOD LCC rows too. Force
            // uniform when those rows are present (their LODs come from the
            // source file instead).
            const useNanogs = useNanogsRequested && N <= NANOGS_BROWSER_MAX && !hasLccRows;
            if (useNanogsRequested && !useNanogs) {
                console.warn(hasLccRows ?
                    '[LCC2] NanoGS requested but the scene contains multi-LOD LCC splats (their LODs are merged from the source) — using uniform.' :
                    `[LCC2] NanoGS requested but N=${N.toLocaleString()} > ${NANOGS_BROWSER_MAX.toLocaleString()} (browser guard) — using uniform. Large scenes auto-route to the backend (no guard).`);
            }
            // Appearance columns = f_dc_* + f_rest_* (matches NanoGS app_names;
            // normals nx,ny,nz are NOT appearance → zeroed on NanoGS output).
            const srcColNames = srcCols.map(c => c.name);
            const shColNames = srcColNames
            .filter(cn => cn.startsWith('f_dc_') || cn.startsWith('f_rest_'));
            // Column lookup by name for nodeAttrsFromColumns (typed-array views).
            const colSrcLookup: ColumnLookup = {};
            for (const col of srcCols) colSrcLookup[col.name] = col.data as Float32Array;
            const nanogsOpts: SimplifyOpts | null = useNanogs ?
                defaultSimplifyOpts({ shCols: shColNames.length }) :
                null;
            if (useNanogs) {
                console.warn(`[LCC2] NanoGS enabled (shCols=${shColNames.length}, cap=${NANOGS_NODE_CAP}, min=${NANOGS_NODE_MIN}) — runs on main thread`);
            }

            // LOD 精细度采样率：LOD0 (finest) = 100%，等比 0.5^k 递减
            // （100/50/25/12.5/6.25/3.125%），与官方 XGRIDS 参考对齐。
            // L=1 时 k=0 → 100% 单 LOD。
            const lodRate = (k: number): number => Math.pow(0.5, k);

            // Pre-compute estimated total splats for weighted progress.
            // In mixed scenes, level k>0 merges the non-LCC decimated rows
            // (ceil(nlN·rate)) with the LCC source's own LOD-k row counts.
            const nlN = N - lccTotal;
            const estLevelSplats = (k: number): number => {
                if (!hasLccRows) return Math.ceil(N * lodRate(k));
                if (k === 0) return N;
                let lcc = 0;
                for (const s of lccPreserveSplats) lcc += s.lodCounts[Math.min(k, s.lodCounts.length - 1)];
                return (nlN > 0 ? Math.ceil(nlN * lodRate(k)) : 0) + lcc;
            };
            let totalEstimatedSplats = 0;
            for (let D = 1; D <= treeDepth; ++D) {
                const k = treeDepth - D;
                totalEstimatedSplats += estLevelSplats(k);
            }
            let cumulativeSplats = 0;

            // Mixed-scene LOD level writer: at level k>0, each node's output
            // = [non-LCC rows stride-sampled at `rate`] + [LCC rows from the
            // preserved LCC splats' OWN LOD level k (streamed from the source
            // file, binned into the tree by AABB)]. This merges each level's
            // corresponding LODs instead of exporting separately. Consecutive
            // depths may map to the same source LOD when numLods < treeDepth;
            // the level table is simply reloaded per depth.
            const writeMixedLevel = async (D: number, k: number): Promise<number> => {
                const rate = lodRate(k);
                const nodesAtD = collectNodesAtDepth(adaptiveRoot, D);
                if (nodesAtD.length === 0) return 0;

                // Load the combined LCC LOD-k table (all preserved splats).
                const lccTables: DataTable[] = [];
                for (const s of lccPreserveSplats) {
                    lccTables.push(await loadLccLodForExport(s, Math.min(k, s.lodCounts.length - 1), exportSettings));
                }
                const lccLevelTable = lccTables.length === 1 ? lccTables[0] : concatDataTables(lccTables);
                applyLcc2CoordinateTransform(lccLevelTable, maxSHBands);
                const lccN = lccLevelTable.numRows;
                if (lccN === 0) {
                    throw new Error('[LCC2] LCC LOD level is empty after deletions');
                }
                const lccCols = lccLevelTable.columns;
                const numCols = lccCols.length;
                const lccXs = lccLevelTable.getColumnByName('x')!.data as Float32Array;
                const lccYs = lccLevelTable.getColumnByName('y')!.data as Float32Array;
                const lccZs = lccLevelTable.getColumnByName('z')!.data as Float32Array;

                // Bin LCC LOD-k rows into depth-D nodes by AABB descent.
                const nodeIndex = new Map<AdaptiveNode, number>();
                for (let i = 0; i < nodesAtD.length; ++i) nodeIndex.set(nodesAtD[i], i);
                const lccCounts: number[] = new Array(nodesAtD.length).fill(0);
                for (let i = 0; i < lccN; ++i) {
                    const node = findAdaptiveNodeAtDepth(adaptiveRoot, lccXs[i], lccYs[i], lccZs[i], D);
                    if (node) {
                        const idx = nodeIndex.get(node);
                        if (idx !== undefined) lccCounts[idx]++;
                    }
                }
                const lccSubsets: (Uint32Array | null)[] = new Array(nodesAtD.length).fill(null);
                for (let i = 0; i < nodesAtD.length; ++i) {
                    if (lccCounts[i] > 0) lccSubsets[i] = new Uint32Array(lccCounts[i]);
                }
                const fill = new Uint32Array(nodesAtD.length);
                for (let i = 0; i < lccN; ++i) {
                    const node = findAdaptiveNodeAtDepth(adaptiveRoot, lccXs[i], lccYs[i], lccZs[i], D);
                    if (node) {
                        const idx = nodeIndex.get(node);
                        if (idx !== undefined && lccSubsets[idx]) lccSubsets[idx]![fill[idx]++] = i;
                    }
                }

                // Per-node combined output: non-LCC rows (stride-sampled at
                // `rate`) followed by the binned LCC rows.
                const nlSubsets: (Uint32Array | null)[] = new Array(nodesAtD.length).fill(null);
                const counts: number[] = new Array(nodesAtD.length);
                let levelTotal = 0;
                for (let i = 0; i < nodesAtD.length; ++i) {
                    const finest = nodesAtD[i].finestIndices;
                    // Positions (within `finest`) of the non-LCC rows.
                    const nlPos: number[] = [];
                    for (let j = 0; j < finest.length; ++j) {
                        if (finest[j] < nlN) nlPos.push(j);
                    }
                    const nlTake = nlPos.length > 0 ? Math.max(1, Math.ceil(nlPos.length * rate)) : 0;
                    if (nlTake > 0) {
                        const nlIdx = new Uint32Array(nlTake);
                        for (let j = 0; j < nlTake; ++j) nlIdx[j] = finest[nlPos[Math.floor(j * nlPos.length / nlTake)]];
                        nlSubsets[i] = nlIdx;
                    }
                    counts[i] = nlTake + lccCounts[i];
                    levelTotal += counts[i];
                }
                if (levelTotal === 0) return 0;

                console.warn(`[LCC2] LOD${k}/depth${D}: ${nodesAtD.length} nodes, ${levelTotal.toLocaleString()} splats (rate=${(rate * 100).toFixed(1)}%, LCC merged)`);

                // ---- Chunk pipeline (two sources: dataTable + lccLevelTable) ----
                const prepareChunk = (startNode: number) => {
                    let estTotal = 0;
                    let endNode = startNode;
                    while (endNode < nodesAtD.length) {
                        const nxt = estTotal + counts[endNode];
                        if (nxt > 0 && nxt > SOG_CHUNK_TARGET) break;
                        estTotal = nxt;
                        endNode++;
                    }
                    if (estTotal === 0) return null;

                    const chunkCols: Column[] = new Array(numCols);
                    for (let ci = 0; ci < numCols; ++ci) {
                        const dst = new Float32Array(estTotal);
                        const nlSrc = dataTable.columns[ci].data as Float32Array;
                        const lccSrc = lccCols[ci].data as Float32Array;
                        let off = 0;
                        for (let n = startNode; n < endNode; ++n) {
                            const nlIdx = nlSubsets[n];
                            if (nlIdx) {
                                for (let j = 0; j < nlIdx.length; ++j) dst[off + j] = nlSrc[nlIdx[j]];
                                off += nlIdx.length;
                            }
                            const lccSub = lccSubsets[n];
                            if (lccSub) {
                                for (let j = 0; j < lccSub.length; ++j) dst[off + j] = lccSrc[lccSub[j]];
                                off += lccSub.length;
                            }
                        }
                        chunkCols[ci] = new Column(lccCols[ci].name, dst);
                    }
                    const chunkTable = new DataTable(chunkCols, dataTable.transform);

                    const identity = new Uint32Array(estTotal);
                    for (let i = 0; i < estTotal; ++i) identity[i] = i;

                    const chunkIdx = chunkIdxByLevel[k]++;
                    const firstNode = nodesAtD[startNode];
                    const lodFileName = `${firstNode.id}.sog`;
                    const chunkPath = `${name}/data/3dgs/${lodFileName}`;
                    console.warn(`[LCC2] LOD${k}/chunk${chunkIdx}: nodes[${startNode}..${endNode - 1}] ${estTotal.toLocaleString()} splats → ${chunkPath}`);
                    return { startNode, endNode, chunkTable, identity, lodFileName, chunkPath, chunkIdx };
                };
                const finalizeChunk = (info: { startNode: number; endNode: number; lodFileName: string; chunkIdx: number }) => {
                    const fileIdx = splatFiles.length;
                    splatFiles.push(`data/3dgs/${info.lodFileName}`);
                    let nodeOffset = 0;
                    for (let n = info.startNode; n < info.endNode; ++n) {
                        if (counts[n] > 0) {
                            nodeRefs.set(nodesAtD[n], { name: fileIdx, start: nodeOffset, count: counts[n] });
                            lodSplatsByLevel[k] += counts[n];
                        }
                        nodeOffset += counts[n];
                    }
                    (globalThis as { gc?: () => void }).gc?.();
                };

                let cursor = 0;
                let nextInfo = prepareChunk(cursor);
                while (nextInfo) {
                    cursor = nextInfo.endNode;
                    const writePromise = writeSogInternal({
                        filename: nextInfo.chunkPath,
                        dataTable: nextInfo.chunkTable,
                        bundle: true,
                        iterations,
                        createDevice: createGpuDevice,
                        indices: nextInfo.identity
                    }, fs);
                    const currentInfo = nextInfo;
                    nextInfo = prepareChunk(cursor);
                    await writePromise;
                    finalizeChunk(currentInfo);
                }

                // Release the level table before the next depth loads a new one.
                (globalThis as { gc?: () => void }).gc?.();
                return levelTotal;
            };

            events?.fire('progressUpdate', { text: `Writing ${treeDepth} LOD levels (${totalEstimatedSplats.toLocaleString()} splats total)…  —  1%`, progress: 1 });

            // --- Build chain-top map (cell-major NanoGS reuse, plan §6 D3) ---
            // Mirror of server/lcc2-export.mjs: the adaptive tree chains identical
            // `finestIndices` across depths (chain continuations share the same
            // Uint32Array reference). The depth-major loop below would otherwise
            // re-simplify the same `finestIndices` once per depth, rebuilding KNN
            // on N0 up to (treeDepth-1) times per cell. By mapping each node to its
            // chain top, we simplify each cell ONCE with all its LOD rates via
            // simplifyProgressive (one KNN pass) and cache per-rate snapshots.
            //
            // A chain ends when the node splits (child has different `finestIndices`)
            // or reaches a leaf; `chainTopEndDepth` bounds which rates we compute.
            const nodeToChainTop = new Map<AdaptiveNode, AdaptiveNode>();
            const chainTopDepth = new Map<AdaptiveNode, number>();
            const chainTopEndDepth = new Map<AdaptiveNode, number>();
            const walkChainTop = (node: AdaptiveNode, parent: AdaptiveNode, parentChainTop: AdaptiveNode | null, depth: number) => {
                const isRootChild = parent === adaptiveRoot;
                const isChainTop = isRootChild || (node.finestIndices !== parent.finestIndices);
                const chainTop = isChainTop ? node : (parentChainTop as AdaptiveNode);
                nodeToChainTop.set(node, chainTop);
                if (isChainTop) {
                    chainTopDepth.set(chainTop, depth);
                    chainTopEndDepth.set(chainTop, depth);
                } else {
                    chainTopEndDepth.set(chainTop, depth);
                }
                if (node.child) {
                    for (const c of node.child) walkChainTop(c, node, chainTop, depth + 1);
                }
            };
            if (adaptiveRoot.child) {
                for (const c of adaptiveRoot.child) walkChainTop(c, adaptiveRoot, null, 1);
            }

            // --- NanoGS snapshot cache (chain top → Map<rate, {cols, count}> | null) ---
            // Populated lazily on first encounter of each chain top. null =
            // NanoGS failed/disabled → uniform fallback per depth.
            type NanoSnapshot = { cols: { [name: string]: Float32Array }; count: number };
            const simplifyCache = new Map<AdaptiveNode, Map<number, NanoSnapshot> | null>();
            const getCachedSnapshot = (node: AdaptiveNode, rate: number): NanoSnapshot | null => {
                const chainTop = nodeToChainTop.get(node) as AdaptiveNode;
                let chainCaches = simplifyCache.get(chainTop);
                if (chainCaches === undefined) {
                    const topDepth = chainTopDepth.get(chainTop) as number;
                    const endDepth = chainTopEndDepth.get(chainTop) as number;
                    const ratesDesc: number[] = [];
                    for (let d = topDepth; d <= endDepth; ++d) {
                        const k = treeDepth - d;
                        const r = lodRate(k);
                        if (r < 1.0) ratesDesc.push(r);
                    }
                    ratesDesc.sort((a, b) => b - a); // descending (finest first)

                    chainCaches = null;
                    if (useNanogs && nanogsOpts && ratesDesc.length > 0) {
                        const tCell = performance.now();
                        try {
                            const results = simplifyNodeBatched(chainTop.finestIndices, colSrcLookup, srcColNames, shColNames, ratesDesc, nanogsOpts);
                            if (results && results.length === ratesDesc.length) {
                                chainCaches = new Map<number, NanoSnapshot>();
                                for (let i = 0; i < ratesDesc.length; ++i) {
                                    chainCaches.set(ratesDesc[i], results[i]);
                                }
                                const cellSize = chainTop.finestIndices.length;
                                const dur = ((performance.now() - tCell) / 1000).toFixed(1);
                                console.warn(`[LCC2] Cell ${chainTop.id} simplified: ${ratesDesc.length} LOD snapshots (${cellSize.toLocaleString()} splats) [${dur}s]`);
                            }
                        } catch (e) {
                            console.warn(`[LCC2] NanoGS cell simplify failed on ${chainTop.id} (${chainTop.finestIndices.length} splats): ${(e as Error).message} — fallback uniform`);
                        }
                    }
                    simplifyCache.set(chainTop, chainCaches);
                }
                return chainCaches ? (chainCaches.get(rate) ?? null) : null;
            };

            // Encode coarsest (depth 1) → finest (depth treeDepth).
            for (let D = 1; D <= treeDepth; ++D) {
                const k = treeDepth - D;     // LOD level index (0=finest)
                const rate = lodRate(k);     // 动态采样率：100% → 10% 线性递减

                // Mixed scene: coarser levels merge the non-LCC decimated rows
                // with the preserved LCC splats' own LOD-k (streamed from the
                // source). The finest level (k=0) is the full combined data —
                // handled by the normal path below.
                if (hasLccRows && k > 0) {
                    const levelTotal = await writeMixedLevel(D, k);
                    cumulativeSplats += levelTotal;
                    const progressPercent = Math.round(100 * cumulativeSplats / totalEstimatedSplats);
                    events?.fire('progressUpdate', {
                        text: `LOD ${k} (${D}/${treeDepth}): ${levelTotal.toLocaleString()} splats  —  ${progressPercent}%`,
                        progress: progressPercent
                    });
                    continue;
                }

                const nodesAtD = collectNodesAtDepth(adaptiveRoot, D);

                // ---- Pass 1: count LOD samples per node (no subset allocation yet) ----
                const counts: number[] = new Array(nodesAtD.length);
                let levelTotal = 0;
                for (let i = 0; i < nodesAtD.length; ++i) {
                    const nodeLen = nodesAtD[i].finestIndices.length;
                    // rate=1 → 全量; rate=0.1 → 10%。至少 1 个点保证非空节点有数据。
                    const cnt = Math.max(1, Math.ceil(nodeLen * rate));
                    counts[i] = cnt;
                    levelTotal += cnt;
                }
                console.warn(`[LCC2] LOD${k}/depth${D}: ${nodesAtD.length} nodes, ${levelTotal.toLocaleString()} splats (rate=${(rate * 100).toFixed(1)}%)`);

                if (levelTotal === 0) {
                    continue;
                }

                // ---- Pass 2: write files in chunks <= SOG_CHUNK_TARGET ----
                // Double-buffered pipeline: while chunk N's writeSogInternal is
                // awaiting worker WebP encoding, the main thread concurrently
                // prepares chunk N+1's chunkTable (pure CPU column subset). Two
                // writeSogInternal calls never overlap (they share one cached
                // GPU device + backbuffer via createGpuDevice), but the CPU
                // gather/subset work is hidden behind the worker WebP time.
                // Peak memory = finest DataTable + current chunk + prefetched
                // next chunk (each ≤ SOG_CHUNK_TARGET splats).
                // Display offset by -1: k=0 (finest, 100%) is the pristine source
                // and not a decimated "LOD level", so it falls back to the raw
                // step name (currentExportLod=-1). Coarser levels k≥1 map to
                // LOD0..LOD(treeDepth-2), matching UE plugin's 0-based LOD indexing
                // where LOD0 is the first decimated level.
                currentExportLod = k - 1;
                lodExportProgress = Math.round(100 * (cumulativeSplats + levelTotal) / totalEstimatedSplats);

                // NanoGS only applies to decimated levels (rate < 1.0). The
                // finest level (rate=1.0) is the pristine source — no merging.
                // Mirrors server/lcc2-export.mjs `depthUsesNanogs`.
                const depthUsesNanogs = useNanogs && rate < 1.0;

                // Resolve one node's LOD splats for the current rate.
                //   NanoGS path (cached): getCachedSnapshot looks up this node's
                //     chain top in `simplifyCache`. On the first depth where the
                //     chain top appears, all its LOD snapshots are computed in one
                //     simplifyNodeBatched call (one KNN pass via simplifyProgressive,
                //     plan §6 D3); deeper depths hit the cache for free. Falls back
                //     to whole-node uniform when NanoGS is disabled, the chain top's
                //     simplify failed (cached null), or the depth is the finest
                //     (rate=1.0). Per plan §4.2/§3.2 + nanogs-large-node-subbatch.md.
                //   Uniform path: stride-sample finestIndices into source cols.
                // Returns { cols, indices, count }: exactly one of cols/indices
                // is set. `cols` (NanoGS) is keyed by PLY property name; missing
                // columns (normals) are zeroed by the caller.
                const resolveNode = (nodeIdx: number): { cols: { [name: string]: Float32Array } | null; indices: Uint32Array | null; count: number } => {
                    const node = nodesAtD[nodeIdx];
                    const finest = node.finestIndices;
                    const nodeLen = finest.length;
                    if (depthUsesNanogs) {
                        const result = getCachedSnapshot(node, rate);
                        if (result && result.count > 0) {
                            return { cols: result.cols, indices: null, count: result.count };
                        }
                    }
                    // uniform fallback / default
                    const cnt = Math.max(1, Math.ceil(nodeLen * rate));
                    const indices = new Uint32Array(cnt);
                    for (let j = 0; j < cnt; ++j) indices[j] = finest[Math.floor(j * nodeLen / cnt)];
                    return { cols: null, indices, count: cnt };
                };

                // Build a chunk's chunkTable + identity indices (pure CPU, no I/O).
                // Returns null when no more nodes remain.
                const prepareChunk = (startNode: number) => {
                    // Decide chunk span using ESTIMATED counts (counts[i] = ceil
                    // (nodeLen*rate)). NanoGS output ≤ this estimate (it merges
                    // down to the same target), so the estimate is a safe upper
                    // bound — chunks never overflow SOG_CHUNK_TARGET.
                    let estTotal = 0;
                    let endNode = startNode;
                    while (endNode < nodesAtD.length) {
                        const nxt = estTotal + counts[endNode];
                        if (nxt > 0 && nxt > SOG_CHUNK_TARGET) break; // don't split a node mid-way
                        estTotal = nxt;
                        endNode++;
                    }
                    if (estTotal === 0) return null;

                    // Resolve each node (NanoGS or uniform), cache results, and
                    // sum the ACTUAL total (NanoGS snapshots may be ≤ estimate).
                    const resolved: { cols: { [name: string]: Float32Array } | null; indices: Uint32Array | null; count: number }[] = new Array(endNode - startNode);
                    let actualTotal = 0;
                    for (let n = startNode; n < endNode; ++n) {
                        const r = resolveNode(n);
                        resolved[n - startNode] = r;
                        actualTotal += r.count;
                    }
                    if (actualTotal === 0) return null;

                    // Dense-fill chunkTable column-by-column from resolved nodes.
                    const chunkCols: Column[] = new Array(numCols);
                    for (let ci = 0; ci < numCols; ++ci) {
                        const colName = srcCols[ci].name;
                        const dst = new Float32Array(actualTotal);
                        let off = 0;
                        for (let i = 0; i < resolved.length; ++i) {
                            const r = resolved[i];
                            const cnt = r.count;
                            if (r.cols) {
                                // NanoGS: merged appearance cols; missing cols
                                // (nx,ny,nz normals) stay 0 (store_ply convention).
                                const src = r.cols[colName];
                                if (src) for (let j = 0; j < cnt; ++j) dst[off + j] = src[j];
                            } else {
                                // uniform: copy from source via stride indices.
                                const src = srcCols[ci].data as Float32Array;
                                const idx = r.indices!;
                                for (let j = 0; j < cnt; ++j) dst[off + j] = src[idx[j]];
                            }
                            off += cnt;
                        }
                        chunkCols[ci] = new Column(colName, dst);
                    }
                    const chunkTable = new DataTable(chunkCols, dataTable.transform);

                    const identity = new Uint32Array(actualTotal);
                    for (let i = 0; i < actualTotal; ++i) identity[i] = i;

                    // Actual per-node counts (NanoGS may differ from estimate).
                    const nodeActualCounts = resolved.map(r => r.count);

                    const chunkIdx = chunkIdxByLevel[k]++;
                    const firstNode = nodesAtD[startNode];
                    const lodFileName = `${firstNode.id}.sog`;
                    const chunkPath = `${name}/data/3dgs/${lodFileName}`;
                    console.warn(`[LCC2] LOD${k}/chunk${chunkIdx}: nodes[${startNode}..${endNode - 1}] ${actualTotal.toLocaleString()} splats → ${chunkPath}`);

                    return { startNode, endNode, chunkTable, identity, lodFileName, chunkPath, chunkIdx, nodeActualCounts };
                };

                // Record splatFiles entry + per-node refs after a chunk's write completes.
                const finalizeChunk = (info: { startNode: number; endNode: number; lodFileName: string; chunkIdx: number; nodeActualCounts: number[] }) => {
                    const fileIdx = splatFiles.length;
                    splatFiles.push(`data/3dgs/${info.lodFileName}`);
                    console.warn(`[LCC2] LOD${k}/chunk${info.chunkIdx}: done`);
                    let nodeOffset = 0;
                    for (let i = 0; i < info.nodeActualCounts.length; ++i) {
                        const n = info.startNode + i;
                        const cnt = info.nodeActualCounts[i];
                        nodeRefs.set(nodesAtD[n], { name: fileIdx, start: nodeOffset, count: cnt });
                        lodSplatsByLevel[k] += cnt;
                        nodeOffset += cnt;
                    }
                    (globalThis as { gc?: () => void }).gc?.();
                };

                // Pipeline: prefetch chunk N+1's chunkTable while chunk N's
                // writeSogInternal awaits worker WebP. writeSogInternal returns
                // a Promise after its synchronous gather/k-means setup reaches
                // the first worker await; the synchronous prepareChunk() for the
                // next chunk then runs concurrently with N's worker WebP.
                let cursor = 0;
                let nextInfo = prepareChunk(cursor);
                while (nextInfo) {
                    cursor = nextInfo.endNode;

                    // Start chunk N's write (returns Promise; runs gather/k-means
                    // then awaits worker WebP).
                    const writePromise = writeSogInternal({
                        filename: nextInfo.chunkPath,
                        dataTable: nextInfo.chunkTable,
                        bundle: true,
                        iterations,
                        createDevice: createGpuDevice,
                        indices: nextInfo.identity
                    }, fs);
                    const currentInfo = nextInfo;

                    // Prefetch chunk N+1's chunkTable on the main thread while
                    // chunk N's worker WebP encodes. This is pure CPU column
                    // copy — does not touch the shared GPU device.
                    nextInfo = prepareChunk(cursor);

                    // Await chunk N complete before starting chunk N+1's write
                    // (shared GPU device — no concurrent writeSogInternal).
                    await writePromise;
                    finalizeChunk(currentInfo);
                }

                cumulativeSplats += levelTotal;
                const progressPercent = Math.round(100 * cumulativeSplats / totalEstimatedSplats);
                events?.fire('progressUpdate', {
                    text: `LOD ${k} (${D}/${treeDepth}): ${levelTotal.toLocaleString()} splats  —  ${progressPercent}%`,
                    progress: progressPercent
                });
            }

            // Finest-first order matching the source/reference convention
            // (lodSplats[0] = LOD0 = finest = largest count). The UE plugin
            // indexes lodSplats by LOD level; reversed order mislabels levels.
            for (let k = 0; k < treeDepth; ++k) lodSplats.push(lodSplatsByLevel[k]);

            // Assemble the nested tree JSON from the adaptive tree.
            root = emitAdaptiveTreeJson(adaptiveRoot, nodeRefs, splatFiles);
        }
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    } finally {
        events?.fire('progressEnd');
    }

    const totalSplats = lodSplats.reduce((a, b) => a + b, 0);

    const meta = {
        version: '0.0.3',
        name: name || 'ReSplat Lcc2 Splats',
        description: 'ReSplat exported LCC2 splats',
        guid,
        source: 'resplat',
        dataType: 'Editor',
        epsg: 0,
        offset: [0, 0, 0],
        shift: [0, 0, 0],
        scale: [1, 1, 1],
        fileType,
        totalSplats,
        lodSplats,
        totalLevels: lodLevels,
        splatType,
        virtualLoD: null as null,
        env: null as null,
        splatExtraAttributes: null as null,
        renderingHints: {
            renderMethod: 'splatting',
            renderMethodVariant: 'ewa',
            sortingMethod: 'depth',
            cameraModel: 'pinhole',
            colorSpace: 'srgb_rec709_display'
        },
        root
    };

    // Write the metadata file
    const jsonWriter = await fs.createWriter(`${name}/${name}.lcc2`);
    await jsonWriter.write(new TextEncoder().encode(JSON.stringify(meta, null, 2)));
    await jsonWriter.close();

    // ZIP fallback: if writing into a MemoryFileSystem (browser without
    // showDirectoryPicker), package the in-memory tree into a ZIP download.
    if (fs instanceof MemoryFileSystem) {
        const downloadFs = new BrowserFileSystem(`${name}.zip`);
        const zipWriter = await downloadFs.createWriter(`${name}.zip`);
        const zipFs = new ZipFileSystem(zipWriter);
        try {
            for (const [filename, data] of fs.results.entries()) {
                const writer = await zipFs.createWriter(filename);
                await writer.write(data);
                await writer.close();
            }
        } finally {
            await zipFs.close();
        }
    }
};

// Multi-LOD LCC2 export from a Splat with a LodEditLog. Unlike serializeLcc2
// (which sub-samples a single DataTable), this streams each LOD level from the
// original LCC file, replays deletions from the LodEditLog, and writes one
// .sog chunk per spatial-tree-node-group per LOD. The adaptive tree is built
// on the finest LOD (LOD 0); coarser LODs are binned into the same tree by
// AABB containment (tree descent per splat).
//
// Memory: peak = finest LOD + one cached coarser LOD. Each LOD is released
// before the next is loaded. GC is hinted between LODs.
//
// Requires splat.lodEditLog, splat.lccFilePath, splat.lccFileSystem, and
// splat.lodCounts.length >= 2. Falls back to serializeLcc2 otherwise.
const serializeLcc2FromLodLog = async (
    splat: Splat,
    options: Lcc2ExportOptions,
    fs: FileSystem
): Promise<void> => {
    const { name, serializeSettings } = options;
    // Default >0: splat-transform's shN k-means skips its Lloyd loop at 0
    // iterations, quantizing every splat to the same SH centroid and breaking
    // view-dependent lighting (blocks render black/white).
    const { iterations = 10, events } = serializeSettings;
    const splatType = options.splatType ?? '.sog';

    // Cap maxSHBands to the source file's actual SH bands. loadLodForExport
    // calls extractDataTable with these settings; if the user requests SH=3
    // (59 cols) but the source LCC2 is "portable" (SH=0, 14 cols), the 45
    // f_rest columns are zero-filled — wasting N×45×4 bytes and pushing peak
    // memory past the ~4 GB renderer limit. For LOD1 (17.8M splats) this is
    // 4.2 GB → "Array buffer allocation failed". Capping to source SH drops
    // LOD1 to 1.0 GB. The source's SH bands are the same across all LODs
    // (same file), so a single upfront detection is valid.
    const requestedSHBands = serializeSettings.maxSHBands ?? 3;
    const sourceMaxSHBands = calcSHBands(getVertexProperties(splat.splatData));
    const maxSHBands = Math.min(requestedSHBands, sourceMaxSHBands);
    if (maxSHBands < requestedSHBands) {
        console.warn(
            `[LCC2] Source SH=${sourceMaxSHBands} < requested SH=${requestedSHBands}; ` +
            `capping to SH=${maxSHBands} (avoids zero-fill column OOM in loadLodForExport)`
        );
    }
    const exportSettings: SerializeSettings = { ...serializeSettings, maxSHBands };

    const { lccFilePath, lccFileSystem, lodCounts, lodEditLog, currentLodIndex } = splat;
    if (!lccFilePath || !lccFileSystem || !lodEditLog) {
        throw new Error('[LCC2] serializeLcc2FromLodLog requires lccFilePath, lccFileSystem, and lodEditLog');
    }
    const numLods = lodCounts.length;
    if (numLods < 2) {
        throw new Error('[LCC2] serializeLcc2FromLodLog requires a multi-LOD source file (numLods >= 2)');
    }

    await fs.mkdir(`${name}/data/3dgs`);
    configureSplatTransform();

    // Show progress immediately — tree building + LOD loading happen before the
    // first writeSogInternal call (which is when createProgressRenderer normally
    // fires its first event). Without this the user sees nothing for ~minutes.
    events?.fire('progressStart', 'Exporting LCC2 (Multi-LOD)');
    events?.fire('progressUpdate', { text: 'Building spatial tree…  —  1%', progress: 1 });

    // Custom progress renderer: unlike createProgressRenderer, this does NOT
    // fire progressStart (dialog reset) or progressEnd (dialog hide) — we
    // manage the dialog lifecycle manually to prevent flickering between LOD
    // levels. Sub-step bar progress is also suppressed to avoid resetting the
    // cumulative percentage. Instead, we show "正在导出 LOD{N}" with the
    // current cumulative progress.
    let currentExportLod = -1;
    let lodExportProgress = 5;
    splatTransformLogger.setRenderer({
        handle: (event: LogEvent) => {
            switch (event.kind) {
                case 'scopeStart':
                    if (event.depth > 0) {
                        const label = currentExportLod >= 0 ? `正在导出 LOD${currentExportLod}` : event.name;
                        events?.fire('progressUpdate', { text: `${label}  —  ${lodExportProgress}%` });
                    }
                    // depth===0: suppress (would fire progressStart, resetting dialog)
                    break;
                case 'scopeEnd':
                    // Suppress — would fire progressEnd, hiding dialog between depths
                    break;
                case 'barStart':
                case 'barTick':
                case 'barEnd':
                    // Suppress sub-step progress — would reset cumulative percentage
                    break;
                case 'message':
                    if (event.level === 'error') console.error(event.text);
                    else if (event.level === 'warn') console.warn(event.text);
                    break;
            }
        }
    });

    // Load a LOD as a PLY-space DataTable (matching extractDataTable's output).
    // For the current LOD, reuse the Splat's data directly only in ordinary
    // multi-LOD mode. In paged proxy mode the resident Splat is merely the
    // display proxy and must never become the source for another exported LOD.
    // Paged exports always stream every level from the original container.
    // Does NOT apply applyLcc2CoordinateTransform — the caller does that.
    const loadLodForExport = async (lodIndex: number, settings: SerializeSettings = exportSettings): Promise<DataTable> => {
        if (lodIndex === currentLodIndex && !splat.pagedLodEditSession) {
            return extractDataTable([splat], settings);
        }

        console.warn(`[LCC2] Loading LOD ${lodIndex} (${lodCounts[lodIndex].toLocaleString()} splats) from ${lccFilePath}`);
        const rawDataTable = await loadLodDataTable(lccFilePath, lccFileSystem, lodIndex);

        // Convert to GSplatData so extractDataTable's SingleSplat/SplatTransformCache
        // can read properties via getProp.
        const gsplatData = dataTableToGSplatData(rawDataTable);

        // Add 'state' property (all 0) for GaussianFilter's deleted/selected checks.
        ensureStateProp(gsplatData);

        // Apply deletions from LodEditLog: mark deleted rows in the state array
        // so GaussianFilter (inside extractDataTable) filters them out.
        const state = gsplatData.getProp('state') as Uint8Array;
        const xs = gsplatData.getProp('x') as Float32Array;
        const ys = gsplatData.getProp('y') as Float32Array;
        const zs = gsplatData.getProp('z') as Float32Array;
        if (!(lodIndex === 0 && splat.pagedLodEditSession)) {
            lodEditLog.applyDeletionsToState(state, xs, ys, zs, splat.worldTransform);
        }
        if (lodIndex === 0 && splat.pagedLodEditSession) {
            for (let i = 0; i < state.length; i++) {
                if (splat.pagedLodEditSession.isSourceDeleted(i)) state[i] |= State.deleted;
            }
        }

        return extractDataTable([makeMockSplat(gsplatData)], settings);
    };

    // Add a zeroed 'state' property (deleted/selected markers) to a GSplatData
    // built from a raw file DataTable, so GaussianFilter can read it.
    const ensureStateProp = (gsplatData: GSplatData): Uint8Array => {
        let state = gsplatData.getProp('state') as Uint8Array | undefined;
        if (!state) {
            state = new Uint8Array(gsplatData.numSplats);
            gsplatData.getElement('vertex').properties.push({
                type: 'uchar',
                name: 'state',
                storage: state,
                byteSize: 1
            });
        }
        return state;
    };

    // Mock Splat: reuse the current Splat's entity (world transform), color
    // grade properties, and provide a transformTexture whose getSource()
    // returns null so SplatTransformCache treats all splats as untransformed
    // (transformIndex = 0 → identity per-splat transform).
    // A temporary page is decoded from a PLY and can have a different source
    // basis from the resident proxy.  Most export paths use the resident
    // entity transform, but paged LOD0 tree construction must use the page's
    // own transform; otherwise the tree is rotated once too many times before
    // applyLcc2CoordinateTransform and all coarse LOD points are binned against
    // the wrong AABB.
    const makeMockSplat = (gsplatData: GSplatData, worldTransform?: Mat4): Splat => ({
        splatData: gsplatData,
        numSplats: gsplatData.numSplats,
        transformTexture: { getSource: (): null => null },
        transformPalette: null,
        entity: worldTransform ? { getWorldTransform: () => worldTransform } : splat.entity,
        tintClr: splat.tintClr,
        temperature: splat.temperature,
        saturation: splat.saturation,
        brightness: splat.brightness,
        blackPoint: splat.blackPoint,
        whitePoint: splat.whitePoint,
        transparency: splat.transparency
    } as unknown as Splat);

    // Build the export tree from paged LOD0 positions when the resident
    // renderer data is only a proxy. Each page is filtered and released before
    // the next one is loaded; only the compact position columns remain for
    // the adaptive tree. This keeps the exact source LOD0 authoritative while
    // avoiding a second full-quality LOD0 GSplat allocation.
    const loadPagedTreeData = async (settings: SerializeSettings): Promise<DataTable> => {
        const session = splat.pagedLodEditSession!;
        const xValues: number[] = [];
        const yValues: number[] = [];
        const zValues: number[] = [];
        for (const page of session.manifest.pages) {
            const loaded = await session.loadPage(page);
            try {
                const pageData = loaded.gsplatData;
                const state = new Uint8Array(pageData.numSplats);
                pageData.getElement('vertex').properties.push({
                    type: 'uchar',
                    name: 'state',
                    storage: state,
                    byteSize: 1
                });
                // In proxy mode the LOD0 source row patch is authoritative.
                // The LodEditLog voxel is captured from the proxy and is only
                // a coarse-LOD replay aid; applying it to a temporary PLY page
                // can delete a much larger or differently-basis region.
                for (let i = 0; i < loaded.sourceIndices.length; i++) {
                    if (session.isSourceDeleted(loaded.sourceIndices[i])) state[i] |= State.deleted;
                }
                const pageTransform = loaded.transform ?
                    new Mat4().setTRS(
                        new Vec3(loaded.transform.translation),
                        new Quat(loaded.transform.rotation),
                        new Vec3(loaded.transform.scale, loaded.transform.scale, loaded.transform.scale)
                    ) : splat.worldTransform;
                const filtered = extractDataTable([makeMockSplat(pageData, pageTransform)], settings);
                const fx = filtered.getColumnByName('x')!.data as Float32Array;
                const fy = filtered.getColumnByName('y')!.data as Float32Array;
                const fz = filtered.getColumnByName('z')!.data as Float32Array;
                for (let i = 0; i < fx.length; i++) {
                    xValues.push(fx[i]);
                    yValues.push(fy[i]);
                    zValues.push(fz[i]);
                }
            } finally {
                session.releasePages([page.id]);
            }
        }
        const count = xValues.length;
        const rot0 = new Float32Array(count);
        rot0.fill(1);
        return new DataTable([
            new Column('x', Float32Array.from(xValues)),
            new Column('y', Float32Array.from(yValues)),
            new Column('z', Float32Array.from(zValues)),
            new Column('rot_0', rot0),
            new Column('rot_1', new Float32Array(count)),
            new Column('rot_2', new Float32Array(count)),
            new Column('rot_3', new Float32Array(count))
        ], Transform.PLY);
    };

    // ---- Build spatial tree from the finest LOD (LOD 0) ----
    // Tree building only needs positions, not SH/color/opacity. Loading the
    // full 59-column DataTable (10.5M × 59 × 4B = 2.5GB) freezes the UI for
    // minutes. Use reduced maxSHBands=0 (14 columns) — the position transform
    // is identical regardless of SH level, so the spatial tree is correct.
    // Pass treeSettings explicitly so the file-load path also uses SH=0
    // (previously the currentLodIndex!==0 branch called loadLodForExport with
    // the full-SH exportSettings, wasting memory on columns the tree ignores).
    const treeSettings: SerializeSettings = { ...serializeSettings, maxSHBands: 0 };
    const treeLodForExport = (lodIndex: number): Promise<DataTable> => loadLodForExport(lodIndex, treeSettings);

    console.warn(`[LCC2] Building spatial tree from finest LOD (${lodCounts[0].toLocaleString()} splats, SH=0)`);
    let treeDataTable = splat.pagedLodEditSession ?
        await loadPagedTreeData(treeSettings) :
        await treeLodForExport(0);
    // H4: treeLodForExport's file-load path holds rawDataTable (LOD 0) + the
    // SH=0 extractDataTable result simultaneously. Hint GC to release
    // rawDataTable/gsplatData before the export loop allocates coarser LODs.
    (globalThis as { gc?: () => void }).gc?.();
    const boundingBox = applyLcc2CoordinateTransform(treeDataTable, maxSHBands);
    const treeN = treeDataTable.numRows;

    if (treeN === 0) {
        throw new Error('[LCC2] No splats to export after applying deletions to finest LOD');
    }

    const fileType = maxSHBands > 0 ? 'quality' : 'portable';
    const guid = crypto.randomUUID().replace(/-/g, '');

    const SOG_CHUNK_TARGET = 3_000_000;
    const SPLIT_TARGET = 500_000;
    const safeTreeDepth = Math.max(1, Math.ceil(Math.log2(treeN / SOG_CHUNK_TARGET)) - 2);
    const userLodLevels = Math.max(1, Math.min(20, options.lodLevels ?? numLods));
    const treeDepth = Math.min(numLods, 20, Math.max(safeTreeDepth, userLodLevels));

    if (treeDepth < userLodLevels) {
        console.warn(`[LCC2] treeDepth capped at numLods=${numLods} (user requested ${userLodLevels})`);
    }
    if (treeDepth > userLodLevels) {
        console.warn(`[LCC2] auto-raised lodLevels ${userLodLevels}→${treeDepth} (N=${treeN.toLocaleString()})`);
    }

    const shFactor = maxSHBands > 0 ? 1 : 0.25;
    const estMin = Math.max(1, Math.round((treeN / 3.8e6) * 10 * shFactor));
    console.warn(`[LCC2] N=${treeN.toLocaleString()} treeDepth=${treeDepth} numLods=${numLods} SH=${maxSHBands} est ~${estMin} min`);

    // Pre-compute total estimated splats for weighted progress reporting.
    // Each LOD's work (load + SOG encode) is roughly proportional to its
    // splat count. Coarse LODs are tiny; the finest LOD dominates.
    let totalEstimatedSplats = 0;
    for (let D = 1; D <= treeDepth; ++D) {
        const k = treeDepth - D;
        const actualLod = Math.min(k, numLods - 1);
        totalEstimatedSplats += lodCounts[actualLod];
    }
    let cumulativeSplats = 0;

    let xs = treeDataTable.getColumnByName('x')!.data as Float32Array;
    let ys = treeDataTable.getColumnByName('y')!.data as Float32Array;
    let zs = treeDataTable.getColumnByName('z')!.data as Float32Array;

    console.warn(`[LCC2] building adaptive tree: N=${treeN.toLocaleString()}, treeDepth=${treeDepth}`);
    const adaptiveRoot = buildAdaptiveLcc2Tree(xs, ys, zs, treeN, treeDepth, boundingBox, SPLIT_TARGET);
    assignAdaptiveNodeIds(adaptiveRoot);
    const depth1Count = adaptiveRoot.child?.length ?? 0;
    console.warn(`[LCC2] adaptive tree done: ${depth1Count} root children`);

    // The SH=0 tree table is only reused below for the finest LOD when the
    // export is portable (SH=0). For quality exports the finest LOD is reloaded
    // with full SH so every .sog chunk shares the same SH layout; release the
    // SH=0 table now so its column buffers don't coexist with the full-SH
    // finest LOD (bounds peak memory).
    if (maxSHBands > 0) {
        treeDataTable = null as unknown as DataTable;
        xs = null as unknown as Float32Array;
        ys = null as unknown as Float32Array;
        zs = null as unknown as Float32Array;
        (globalThis as { gc?: () => void }).gc?.();
    }

    events?.fire('progressUpdate', { text: `Writing ${treeDepth} LOD levels (${totalEstimatedSplats.toLocaleString()} splats total)…  —  5%`, progress: 5 });

    const splatFiles: string[] = [];
    const lodSplats: number[] = [];
    const nodeRefs = new Map<AdaptiveNode, Lcc2NodeRef>();
    const lodSplatsByLevel = new Array(treeDepth).fill(0);
    const chunkIdxByLevel = new Array(treeDepth).fill(0);

    // Cache the last loaded non-finest LOD to avoid reloading when consecutive
    // depths map to the same LOD (happens when numLods < treeDepth).
    let cachedLodIndex = -1;
    let cachedLodDataTable: DataTable | null = null;

    // ---- Streaming finest LOD (multi-LOD source, quality export) ----
    // Materializing the full 59-column LOD 0 (43.4M × 59 × 4B ≈ 9.7 GB) plus
    // extractDataTable's copy (~9.7 GB) blows past renderer RAM on large scenes
    // ("Array buffer allocation failed" — see the 石油小镇.lcc 43M export log).
    // Instead, open the source as a lazy ChunkSource and gather ONLY the rows
    // each output .sog chunk needs (the LCC reader range-reads the requested
    // records from data.bin/shcoef.bin on demand). Peak memory drops to ~one
    // output chunk + one gathered chunk, independent of LOD-0 size.
    //
    // The tree's finestIndices index the FILTERED LOD-0 table (rows surviving
    // deletions + minOpacity + removeInvalid in the tree-build extract), so:
    //   Pass 1 streams the source chunk-by-chunk through the same
    //     GaussianFilter and records the filtered→raw index map (N×4B).
    //   Pass 2 packs nodes into ≤SOG_CHUNK_TARGET chunks, gathers their raw
    //     rows, runs the same mock-splat extract as the coarser LODs (identical
    //     SH layout / color grade / PLY space), transforms to LCC2 space, and
    //     writes the .sog — row order preserved by the identity indices.
    const writeFinestLodStreamed = async (D: number): Promise<number> => {
        // Drop the previous coarser LOD's table before gathering (frees ~4.9 GB
        // at LOD 1) so the mapping pass runs on a near-empty heap.
        if (cachedLodDataTable) {
            cachedLodDataTable = null;
            (globalThis as { gc?: () => void }).gc?.();
        }
        cachedLodIndex = -1;

        const { source: src0, close: closeSrc } = await openLodSource(lccFilePath, lccFileSystem, 0);
        const pool = createChunkDataPool({ chunkSize: Math.max(src0.meta.chunkSize, SOG_CHUNK_TARGET) });
        try {
            const { meta } = src0;
            const lodN = meta.lodCounts[0];
            const nodesAtD = collectNodesAtDepth(adaptiveRoot, D);
            const filter = new GaussianFilter(exportSettings);

            // --- Pass 1: filtered→raw index map ---
            const filteredToRaw = new Uint32Array(treeN);
            let g = 0;
            const numChunks = meta.numChunks[0] ?? Math.ceil(lodN / meta.chunkSize);
            for (let c = 0; c < numChunks; ++c) {
                const cnt = Math.min(meta.chunkSize, lodN - c * meta.chunkSize);
                const rawBase = c * meta.chunkSize;
                const range = new Uint32Array(cnt);
                for (let i = 0; i < cnt; ++i) range[i] = rawBase + i;
                const chunkTable = await gatherSourceRowsToDataTable(src0, pool, range);
                const gsplat = dataTableToGSplatData(chunkTable);
                const state = ensureStateProp(gsplat);
                const x = gsplat.getProp('x') as Float32Array;
                const y = gsplat.getProp('y') as Float32Array;
                const z = gsplat.getProp('z') as Float32Array;
                // Paged LOD0 is filtered by exact stable source rows below.
                // Do not replay the proxy voxel here: it is intentionally not
                // the authoritative LOD0 deletion representation.
                if (!splat.pagedLodEditSession) {
                    lodEditLog.applyDeletionsToState(state, x, y, z, splat.worldTransform);
                }
                if (splat.pagedLodEditSession) {
                    for (let i = 0; i < state.length; i++) {
                        if (splat.pagedLodEditSession.isSourceDeleted(rawBase + i)) state[i] |= State.deleted;
                    }
                }
                filter.set(makeMockSplat(gsplat));
                for (let i = 0; i < cnt; ++i) {
                    if (filter.test(i)) filteredToRaw[g++] = rawBase + i;
                }
                (globalThis as { gc?: () => void }).gc?.();
            }
            if (g !== treeN) {
                throw new Error(
                    `[LCC2] streaming LOD 0: filter kept ${g.toLocaleString()} rows, tree built on ${treeN.toLocaleString()} — aborting`
                );
            }
            (globalThis as { gc?: () => void }).gc?.();

            // --- Pass 2: pack nodes into output chunks, gather + write ---
            currentExportLod = -1; // k=0 → pristine source, no LOD label
            let levelTotal = 0;
            let chunkNodeStart = 0;
            while (chunkNodeStart < nodesAtD.length) {
                // Pack nodes until the chunk would exceed SOG_CHUNK_TARGET
                // (mirrors prepareChunk's packing in the non-streaming path).
                let chunkSplatCount = 0;
                let chunkNodeEnd = chunkNodeStart;
                while (chunkNodeEnd < nodesAtD.length) {
                    const nxt = chunkSplatCount + nodesAtD[chunkNodeEnd].finestIndices.length;
                    if (nxt > 0 && nxt > SOG_CHUNK_TARGET) break;
                    chunkSplatCount = nxt;
                    chunkNodeEnd++;
                }
                if (chunkSplatCount === 0) { // a single node larger than the target
                    chunkSplatCount = nodesAtD[chunkNodeStart].finestIndices.length;
                    chunkNodeEnd = chunkNodeStart + 1;
                }

                // Map the nodes' filtered indices to raw source rows.
                const rawIndices = new Uint32Array(chunkSplatCount);
                let wi = 0;
                for (let n = chunkNodeStart; n < chunkNodeEnd; ++n) {
                    const sub = nodesAtD[n].finestIndices;
                    for (let i = 0; i < sub.length; ++i) rawIndices[wi++] = filteredToRaw[sub[i]];
                }

                // Gather exactly these rows (source space, source SH layout).
                const gathered = await gatherSourceRowsToDataTable(src0, pool, rawIndices);
                const gsplat = dataTableToGSplatData(gathered);
                ensureStateProp(gsplat); // all 0 — rows already passed the filter
                // Same mock-splat extract as the coarser LODs → identical SH
                // layout, color grade and PLY-space output across all .sog files.
                const outTable = extractDataTable([makeMockSplat(gsplat)], exportSettings);
                applyLcc2CoordinateTransform(outTable, maxSHBands);

                const chunkIdx = chunkIdxByLevel[0]++;
                const firstNode = nodesAtD[chunkNodeStart];
                const lodFileName = `${firstNode.id}.sog`;
                const chunkPath = `${name}/data/3dgs/${lodFileName}`;
                console.warn(`[LCC2] LOD0/chunk${chunkIdx}: nodes[${chunkNodeStart}..${chunkNodeEnd - 1}] ${chunkSplatCount.toLocaleString()} splats → ${chunkPath} (streamed)`);

                // CRITICAL: identity indices keep the chunk's row order (the
                // writeSog Morton sort would scramble nodeRefs start/count).
                const identity = new Uint32Array(chunkSplatCount);
                for (let i = 0; i < chunkSplatCount; ++i) identity[i] = i;
                await writeSogInternal({
                    filename: chunkPath,
                    dataTable: outTable,
                    bundle: true,
                    iterations,
                    createDevice: createGpuDevice,
                    indices: identity
                }, fs);

                const fileIdx = splatFiles.length;
                splatFiles.push(`data/3dgs/${lodFileName}`);
                let nodeOffset = 0;
                for (let n = chunkNodeStart; n < chunkNodeEnd; ++n) {
                    const cnt = nodesAtD[n].finestIndices.length;
                    if (cnt > 0) {
                        nodeRefs.set(nodesAtD[n], { name: fileIdx, start: nodeOffset, count: cnt });
                        lodSplatsByLevel[0] += cnt;
                    }
                    nodeOffset += cnt;
                }
                levelTotal += chunkSplatCount;
                chunkNodeStart = chunkNodeEnd;

                const progressPercent = 5 + Math.round(95 * (cumulativeSplats + levelTotal) / totalEstimatedSplats);
                events?.fire('progressUpdate', {
                    text: `LOD 0 (${D}/${treeDepth}): ${levelTotal.toLocaleString()} splats  —  ${progressPercent}%`,
                    progress: progressPercent
                });
                lodExportProgress = progressPercent;
                (globalThis as { gc?: () => void }).gc?.();
            }
            return levelTotal;
        } finally {
            pool.destroy();
            await closeSrc();
        }
    };

    try {
        // ---- Per-depth chunk writing ----
        // Encode coarsest (depth 1) → finest (depth treeDepth).
        for (let D = 1; D <= treeDepth; ++D) {
            const k = treeDepth - D;          // LOD level index (0=finest)
            const actualLod = Math.min(k, numLods - 1);

            // Streaming finest LOD: multi-LOD source + quality export + the
            // finest LOD is not the in-memory current LOD (the in-memory path
            // applies per-splat transforms, which file-loaded rows don't carry).
            if (actualLod === 0 && currentLodIndex !== 0 && (maxSHBands > 0 || splat.pagedLodEditSession)) {
                const levelTotal = await writeFinestLodStreamed(D);
                cumulativeSplats += levelTotal;
                const progressPercent = 5 + Math.round(95 * cumulativeSplats / totalEstimatedSplats);
                events?.fire('progressUpdate', {
                    text: `LOD 0 (${D}/${treeDepth}): ${levelTotal.toLocaleString()} splats  —  ${progressPercent}%`,
                    progress: progressPercent
                });
                lodExportProgress = progressPercent;
                continue;
            }

            // Resolve the LOD DataTable for this depth.
            let lodDataTable: DataTable;
            if (actualLod === cachedLodIndex && cachedLodDataTable) {
                lodDataTable = cachedLodDataTable;
            } else {
                // Release the previous cached LOD before loading a new one.
                if (cachedLodDataTable) {
                    cachedLodDataTable = null;
                    (globalThis as { gc?: () => void }).gc?.();
                }
                if (actualLod === 0 && maxSHBands === 0 && !splat.pagedLodEditSession) {
                    // Portable (SH=0) source: reuse the tree-building table
                    // directly. It already holds the finest LOD in LCC2 space
                    // with the same SH=0 layout as the coarser LODs, and its
                    // `finestIndices` are valid row indices into it.
                    lodDataTable = treeDataTable;
                } else {
                    // Load this LOD (including the finest) with the full SH
                    // layout so every .sog chunk shares the same SH bands.
                    // Reusing the SH=0 tree table for the finest LOD of a
                    // quality source would mismatch the coarser LODs' SH bands
                    // and break the LCC2 reader ("SH band mismatch").
                    lodDataTable = await loadLodForExport(actualLod);
                    // H4: loadLodForExport's file-load path leaves rawDataTable +
                    // gsplatData (the full source-LOD DataTable) GC-eligible but
                    // not yet collected. Hint GC before the chunk-prep loop
                    // allocates per-chunk Float32Array copies, so peak memory is
                    // lodDataTable + treeDataTable instead of + rawDataTable.
                    (globalThis as { gc?: () => void }).gc?.();
                }
                cachedLodIndex = actualLod;
                cachedLodDataTable = lodDataTable;
            }

            // Convert to LCC2 space (in-place). LODs loaded from file (coarser
            // LODs and the full-SH finest LOD) arrive in PLY space and need the
            // transform. The tree-building table (reused only for the SH=0
            // finest LOD) is already in LCC2 space.
            if (lodDataTable !== treeDataTable) {
                applyLcc2CoordinateTransform(lodDataTable, maxSHBands);
            }

            const lodN = lodDataTable.numRows;
            const lodXs = lodDataTable.getColumnByName('x')!.data as Float32Array;
            const lodYs = lodDataTable.getColumnByName('y')!.data as Float32Array;
            const lodZs = lodDataTable.getColumnByName('z')!.data as Float32Array;
            const lodCols = lodDataTable.columns;
            const numCols = lodCols.length;

            const nodesAtD = collectNodesAtDepth(adaptiveRoot, D);

            // ---- Bin LOD splats into depth-D nodes ----
            // For the finest LOD (actualLod===0), the tree's `finestIndices` are
            // already valid indices into `lodDataTable` (same row order as the
            // tree-building DataTable — extractDataTable filters rows identically
            // regardless of SH settings). Use them directly — O(1) per node.
            // For coarser LODs, descend the tree per splat to find the containing
            // depth-D node by AABB. O(D × max_children) per splat.
            const nodeSubsets: (Uint32Array | null)[] = new Array(nodesAtD.length).fill(null);
            const counts: number[] = new Array(nodesAtD.length).fill(0);
            let levelTotal = 0;

            if (actualLod === 0) {
                // Finest LOD: finestIndices ARE the LOD-0 indices (step = 1).
                for (let i = 0; i < nodesAtD.length; ++i) {
                    const sub = nodesAtD[i].finestIndices;
                    nodeSubsets[i] = sub;
                    counts[i] = sub.length;
                    levelTotal += sub.length;
                }
            } else {
                // Coarser LOD: bin by AABB descent.
                // Map node → index for O(1) lookup during descent.
                const nodeIndex = new Map<AdaptiveNode, number>();
                for (let i = 0; i < nodesAtD.length; ++i) nodeIndex.set(nodesAtD[i], i);

                // Pass 1: count splats per node via tree descent.
                for (let i = 0; i < lodN; ++i) {
                    const node = findAdaptiveNodeAtDepth(adaptiveRoot, lodXs[i], lodYs[i], lodZs[i], D);
                    if (node) {
                        const idx = nodeIndex.get(node);
                        if (idx !== undefined) counts[idx]++;
                    }
                }
                // Allocate subset arrays.
                for (let i = 0; i < nodesAtD.length; ++i) {
                    if (counts[i] > 0) {
                        nodeSubsets[i] = new Uint32Array(counts[i]);
                        levelTotal += counts[i];
                    }
                }
                // Pass 2: fill subsets.
                const fill = new Uint32Array(nodesAtD.length);
                for (let i = 0; i < lodN; ++i) {
                    const node = findAdaptiveNodeAtDepth(adaptiveRoot, lodXs[i], lodYs[i], lodZs[i], D);
                    if (node) {
                        const idx = nodeIndex.get(node);
                        if (idx !== undefined && nodeSubsets[idx]) {
                            nodeSubsets[idx]![fill[idx]++] = i;
                        }
                    }
                }
                levelTotal += backfillEmptyLodNodes(nodesAtD, counts, nodeSubsets, lodXs, lodYs, lodZs, lodN);
            }
            console.warn(`[LCC2] LOD${k}/depth${D}: ${nodesAtD.length} nodes, ${levelTotal.toLocaleString()} splats`);

            if (levelTotal === 0) {
                continue;
            }

            // Tell the progress renderer which LOD is being written. Pre-
            // compute the expected final progress so "正在导出 LOD{N}" shows
            // the destination percentage during SOG encoding.
            // Display offset by -1: k=0 (finest, 100%) is the pristine source
            // and not a decimated "LOD level", so it falls back to the raw
            // step name (currentExportLod=-1). Coarser levels k≥1 map to
            // LOD0..LOD(treeDepth-2), matching UE plugin's 0-based LOD indexing.
            currentExportLod = k - 1;
            lodExportProgress = 5 + Math.round(95 * (cumulativeSplats + levelTotal) / totalEstimatedSplats);

            // ---- Write files in chunks <= SOG_CHUNK_TARGET ----
            // Double-buffered pipeline: while chunk N's writeSogInternal awaits
            // worker WebP encoding, the main thread concurrently prepares chunk
            // N+1's chunkTable (pure CPU column subset). Two writeSogInternal
            // calls never overlap (shared cached GPU device). See serializeLcc2
            // for the same pattern. Peak memory = lodDataTable + current chunk
            // + prefetched next chunk.
            const prepareChunk = (startNode: number) => {
                let chunkSplatCount = 0;
                let endNode = startNode;
                while (endNode < nodesAtD.length) {
                    const nxt = chunkSplatCount + counts[endNode];
                    if (nxt > 0 && nxt > SOG_CHUNK_TARGET) break;
                    chunkSplatCount = nxt;
                    endNode++;
                }
                if (chunkSplatCount === 0) return null;

                const chunkIndices = new Uint32Array(chunkSplatCount);
                let wi = 0;
                for (let n = startNode; n < endNode; ++n) {
                    const sub = nodeSubsets[n];
                    if (sub) {
                        chunkIndices.set(sub, wi);
                        wi += counts[n];
                    }
                }

                // Column-by-column from source for memory safety.
                const chunkCols: Column[] = new Array(numCols);
                for (let ci = 0; ci < numCols; ++ci) {
                    const src = lodCols[ci].data as Float32Array;
                    const dst = new Float32Array(chunkSplatCount);
                    for (let i = 0; i < chunkSplatCount; ++i) dst[i] = src[chunkIndices[i]];
                    chunkCols[ci] = new Column(lodCols[ci].name, dst);
                }
                const chunkTable = new DataTable(chunkCols, lodDataTable.transform);

                const identity = new Uint32Array(chunkSplatCount);
                for (let i = 0; i < chunkSplatCount; ++i) identity[i] = i;

                const chunkIdx = chunkIdxByLevel[k]++;
                const firstNode = nodesAtD[startNode];
                const lodFileName = `${firstNode.id}.sog`;
                const chunkPath = `${name}/data/3dgs/${lodFileName}`;
                console.warn(`[LCC2] LOD${k}/chunk${chunkIdx}: nodes[${startNode}..${endNode - 1}] ${chunkSplatCount.toLocaleString()} splats → ${chunkPath}`);

                return { startNode, endNode, chunkTable, identity, lodFileName, chunkPath, chunkIdx };
            };

            const finalizeChunk = (info: { startNode: number; endNode: number; lodFileName: string; chunkIdx: number }) => {
                const fileIdx = splatFiles.length;
                splatFiles.push(`data/3dgs/${info.lodFileName}`);
                console.warn(`[LCC2] LOD${k}/chunk${info.chunkIdx}: done`);

                let nodeOffset = 0;
                for (let n = info.startNode; n < info.endNode; ++n) {
                    if (counts[n] > 0) {
                        nodeRefs.set(nodesAtD[n], { name: fileIdx, start: nodeOffset, count: counts[n] });
                        lodSplatsByLevel[k] += counts[n];
                    }
                    nodeOffset += counts[n];
                }
                (globalThis as { gc?: () => void }).gc?.();
            };

            // Pipeline: prefetch chunk N+1's chunkTable while chunk N's
            // writeSogInternal awaits worker WebP.
            let cursor = 0;
            let nextInfo = prepareChunk(cursor);
            while (nextInfo) {
                cursor = nextInfo.endNode;

                const writePromise = writeSogInternal({
                    filename: nextInfo.chunkPath,
                    dataTable: nextInfo.chunkTable,
                    bundle: true,
                    iterations,
                    createDevice: createGpuDevice,
                    indices: nextInfo.identity
                }, fs);
                const currentInfo = nextInfo;

                // Prefetch chunk N+1's chunkTable (pure CPU) while chunk N's
                // worker WebP encodes.
                nextInfo = prepareChunk(cursor);

                // Await chunk N complete before starting chunk N+1's write
                // (shared GPU device — no concurrent writeSogInternal).
                await writePromise;
                finalizeChunk(currentInfo);
            }

            cumulativeSplats += levelTotal;
            const progressPercent = 5 + Math.round(95 * cumulativeSplats / totalEstimatedSplats);
            events?.fire('progressUpdate', {
                text: `LOD ${k} (${D}/${treeDepth}): ${levelTotal.toLocaleString()} splats  —  ${progressPercent}%`,
                progress: progressPercent
            });
            lodExportProgress = progressPercent;
        }

        // Release cached LOD data.
        if (cachedLodDataTable) {
            cachedLodDataTable = null;
            (globalThis as { gc?: () => void }).gc?.();
        }

        // Finest-first order matching the source/reference convention
        // (lodSplats[0] = LOD0 = finest = largest count). The UE plugin
        // indexes lodSplats by LOD level; reversed order mislabels levels.
        for (let k = 0; k < treeDepth; ++k) lodSplats.push(lodSplatsByLevel[k]);

        // Assemble the nested tree JSON from the adaptive tree.
        const root = emitAdaptiveTreeJson(adaptiveRoot, nodeRefs, splatFiles);

        const totalSplats = lodSplats.reduce((a, b) => a + b, 0);

        const meta = {
            version: '0.0.3',
            name: name || 'ReSplat Lcc2 Splats',
            description: 'ReSplat exported LCC2 splats (multi-LOD from LodEditLog)',
            guid,
            source: 'resplat',
            dataType: 'Editor',
            epsg: 0,
            offset: [0, 0, 0],
            shift: [0, 0, 0],
            scale: [1, 1, 1],
            fileType,
            totalSplats,
            lodSplats,
            totalLevels: treeDepth,
            splatType,
            virtualLoD: null as null,
            env: null as null,
            splatExtraAttributes: null as null,
            renderingHints: {
                renderMethod: 'splatting',
                renderMethodVariant: 'ewa',
                sortingMethod: 'depth',
                cameraModel: 'pinhole',
                colorSpace: 'srgb_rec709_display'
            },
            root
        };

        // Write the metadata file
        const jsonWriter = await fs.createWriter(`${name}/${name}.lcc2`);
        await jsonWriter.write(new TextEncoder().encode(JSON.stringify(meta, null, 2)));
        await jsonWriter.close();

        // ZIP fallback (same as serializeLcc2).
        if (fs instanceof MemoryFileSystem) {
            const downloadFs = new BrowserFileSystem(`${name}.zip`);
            const zipWriter = await downloadFs.createWriter(`${name}.zip`);
            const zipFs = new ZipFileSystem(zipWriter);
            try {
                for (const [filename, data] of fs.results.entries()) {
                    const writer = await zipFs.createWriter(filename);
                    await writer.write(data);
                    await writer.close();
                }
            } finally {
                await zipFs.close();
            }
        }
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    } finally {
        events?.fire('progressEnd');
    }
};

// Serialize an in-memory list of splats into a multi-LOD LCC2 directory. Each
// splat is treated as one pre-built LOD level (splats[0] = finest LOD 0,
// splats[N-1] = coarsest). Unlike serializeLcc2 (which generates LODs by
// simplification from a single fine model), this combines independently
// supplied LOD files — e.g. the "combine multi-LOD LCC2" file-menu action.
//
// This mirrors serializeLcc2FromLodLog's tree-building / per-depth chunk
// writing, but the LOD source is simply `extractDataTable([splats[k]])` rather
// than streaming a multi-LOD LCC file with a LodEditLog. Deletions/edits do
// not apply because the inputs are pristine files, not edited scene splats.
const serializeLcc2FromLodSplats = async (
    splats: Splat[],
    options: Lcc2ExportOptions,
    fs: FileSystem
): Promise<void> => {
    const { name, serializeSettings } = options;
    const { iterations = 10, events } = serializeSettings;
    const splatType = options.splatType ?? '.sog';

    if (splats.length < 2) {
        throw new Error('[LCC2] combine requires at least 2 LOD files');
    }
    const numLods = splats.length;
    const lodCounts = splats.map(s => s.numSplats);

    // Cap maxSHBands to the finest file's actual SH bands (all LODs of the
    // same model share the same SH layout). Prevents zero-fill OOM for
    // "portable" (SH=0) inputs when the caller requested SH=3.
    const requestedSHBands = serializeSettings.maxSHBands ?? 3;
    const sourceMaxSHBands = calcSHBands(getVertexProperties(splats[0].splatData));
    const maxSHBands = Math.min(requestedSHBands, sourceMaxSHBands);
    if (maxSHBands < requestedSHBands) {
        console.warn(
            `[LCC2] Source SH=${sourceMaxSHBands} < requested SH=${requestedSHBands}; ` +
            `capping to SH=${maxSHBands} (avoids zero-fill column OOM)`
        );
    }
    const exportSettings: SerializeSettings = { ...serializeSettings, maxSHBands };

    await fs.mkdir(`${name}/data/3dgs`);
    configureSplatTransform();

    events?.fire('progressStart', 'Exporting LCC2 (Multi-LOD)');
    events?.fire('progressUpdate', { text: 'Building spatial tree…  —  1%', progress: 1 });

    // Same manual progress lifecycle as serializeLcc2FromLodLog — avoid
    // progressStart/progressEnd from the shared renderer resetting the dialog.
    let currentExportLod = -1;
    let lodExportProgress = 5;
    splatTransformLogger.setRenderer({
        handle: (event: LogEvent) => {
            switch (event.kind) {
                case 'scopeStart':
                    if (event.depth > 0) {
                        const label = currentExportLod >= 0 ? `正在导出 LOD${currentExportLod}` : event.name;
                        events?.fire('progressUpdate', { text: `${label}  —  ${lodExportProgress}%` });
                    }
                    break;
                case 'scopeEnd':
                case 'barStart':
                case 'barTick':
                case 'barEnd':
                    break;
                case 'message':
                    if (event.level === 'error') console.error(event.text);
                    else if (event.level === 'warn') console.warn(event.text);
                    break;
            }
        }
    });

    // Load a LOD as a PLY-space DataTable. Does NOT apply
    // applyLcc2CoordinateTransform — the caller does that.
    const loadLodForExport = (lodIndex: number, settings: SerializeSettings = exportSettings): DataTable => {
        return extractDataTable([splats[lodIndex]], settings);
    };

    // Build the spatial tree from the finest LOD with SH=0 (position-only) to
    // keep memory low, matching serializeLcc2FromLodLog's treeSettings path.
    const treeSettings: SerializeSettings = { ...serializeSettings, maxSHBands: 0 };
    console.warn(`[LCC2] Building spatial tree from finest LOD (${lodCounts[0].toLocaleString()} splats, SH=0)`);
    const treeDataTable = loadLodForExport(0, treeSettings);
    (globalThis as { gc?: () => void }).gc?.();
    const boundingBox = applyLcc2CoordinateTransform(treeDataTable, maxSHBands);
    const treeN = treeDataTable.numRows;

    if (treeN === 0) {
        throw new Error('[LCC2] No splats to export in finest LOD');
    }

    const fileType = maxSHBands > 0 ? 'quality' : 'portable';
    const guid = crypto.randomUUID().replace(/-/g, '');

    const SOG_CHUNK_TARGET = 3_000_000;
    const SPLIT_TARGET = 500_000;
    const safeTreeDepth = Math.max(1, Math.ceil(Math.log2(treeN / SOG_CHUNK_TARGET)) - 2);
    const userLodLevels = Math.max(1, Math.min(20, options.lodLevels ?? numLods));
    const treeDepth = Math.min(numLods, 20, Math.max(safeTreeDepth, userLodLevels));

    if (treeDepth < userLodLevels) {
        console.warn(`[LCC2] treeDepth capped at numLods=${numLods} (user requested ${userLodLevels})`);
    }

    // Pre-compute total estimated splats for weighted progress.
    let totalEstimatedSplats = 0;
    for (let D = 1; D <= treeDepth; ++D) {
        const k = treeDepth - D;
        totalEstimatedSplats += lodCounts[Math.min(k, numLods - 1)];
    }
    let cumulativeSplats = 0;

    const xs = treeDataTable.getColumnByName('x')!.data as Float32Array;
    const ys = treeDataTable.getColumnByName('y')!.data as Float32Array;
    const zs = treeDataTable.getColumnByName('z')!.data as Float32Array;

    console.warn(`[LCC2] building adaptive tree: N=${treeN.toLocaleString()}, treeDepth=${treeDepth}`);
    const adaptiveRoot = buildAdaptiveLcc2Tree(xs, ys, zs, treeN, treeDepth, boundingBox, SPLIT_TARGET);
    assignAdaptiveNodeIds(adaptiveRoot);
    events?.fire('progressUpdate', { text: `Writing ${treeDepth} LOD levels (${totalEstimatedSplats.toLocaleString()} splats total)…  —  5%`, progress: 5 });

    const splatFiles: string[] = [];
    const lodSplats: number[] = [];
    const nodeRefs = new Map<AdaptiveNode, Lcc2NodeRef>();
    const lodSplatsByLevel = new Array(treeDepth).fill(0);
    const chunkIdxByLevel = new Array(treeDepth).fill(0);

    try {
        // ---- Per-depth chunk writing (coarsest → finest) ----
        for (let D = 1; D <= treeDepth; ++D) {
            const k = treeDepth - D;          // LOD level index (0=finest)
            const actualLod = Math.min(k, numLods - 1);

            // Resolve the LOD DataTable for this depth. Each imported file is
            // already a distinct LOD, so every level is exported at full
            // fidelity with no simplification / SH down-sampling. The finest
            // LOD must NOT reuse the SH=0 treeDataTable — that would strip its
            // SH/color. Row order is identical to the tree table (extractDataTable
            // filters rows the same regardless of SH settings), so the tree's
            // `finestIndices` remain valid.
            let lodDataTable: DataTable;
            if (actualLod === 0) {
                lodDataTable = loadLodForExport(0);
            } else {
                lodDataTable = loadLodForExport(actualLod);
            }
            applyLcc2CoordinateTransform(lodDataTable, maxSHBands);

            const lodN = lodDataTable.numRows;
            const lodXs = lodDataTable.getColumnByName('x')!.data as Float32Array;
            const lodYs = lodDataTable.getColumnByName('y')!.data as Float32Array;
            const lodZs = lodDataTable.getColumnByName('z')!.data as Float32Array;
            const lodCols = lodDataTable.columns;
            const numCols = lodCols.length;

            const nodesAtD = collectNodesAtDepth(adaptiveRoot, D);

            // Bin LOD splats into depth-D nodes.
            const nodeSubsets: (Uint32Array | null)[] = new Array(nodesAtD.length).fill(null);
            const counts: number[] = new Array(nodesAtD.length).fill(0);
            let levelTotal = 0;

            if (actualLod === 0) {
                for (let i = 0; i < nodesAtD.length; ++i) {
                    const sub = nodesAtD[i].finestIndices;
                    nodeSubsets[i] = sub;
                    counts[i] = sub.length;
                    levelTotal += sub.length;
                }
            } else {
                const nodeIndex = new Map<AdaptiveNode, number>();
                for (let i = 0; i < nodesAtD.length; ++i) nodeIndex.set(nodesAtD[i], i);

                for (let i = 0; i < lodN; ++i) {
                    const node = findAdaptiveNodeAtDepth(adaptiveRoot, lodXs[i], lodYs[i], lodZs[i], D);
                    if (node) {
                        const idx = nodeIndex.get(node);
                        if (idx !== undefined) counts[idx]++;
                    }
                }
                for (let i = 0; i < nodesAtD.length; ++i) {
                    if (counts[i] > 0) {
                        nodeSubsets[i] = new Uint32Array(counts[i]);
                        levelTotal += counts[i];
                    }
                }
                const fill = new Uint32Array(nodesAtD.length);
                for (let i = 0; i < lodN; ++i) {
                    const node = findAdaptiveNodeAtDepth(adaptiveRoot, lodXs[i], lodYs[i], lodZs[i], D);
                    if (node) {
                        const idx = nodeIndex.get(node);
                        if (idx !== undefined && nodeSubsets[idx]) {
                            nodeSubsets[idx]![fill[idx]++] = i;
                        }
                    }
                }
                levelTotal += backfillEmptyLodNodes(nodesAtD, counts, nodeSubsets, lodXs, lodYs, lodZs, lodN);
            }
            console.warn(`[LCC2] LOD${k}/depth${D}: ${nodesAtD.length} nodes, ${levelTotal.toLocaleString()} splats`);

            if (levelTotal === 0) {
                continue;
            }

            currentExportLod = k - 1;
            lodExportProgress = 5 + Math.round(95 * (cumulativeSplats + levelTotal) / totalEstimatedSplats);

            const prepareChunk = (startNode: number) => {
                let chunkSplatCount = 0;
                let endNode = startNode;
                while (endNode < nodesAtD.length) {
                    const nxt = chunkSplatCount + counts[endNode];
                    if (nxt > 0 && nxt > SOG_CHUNK_TARGET) break;
                    chunkSplatCount = nxt;
                    endNode++;
                }
                if (chunkSplatCount === 0) return null;

                const chunkIndices = new Uint32Array(chunkSplatCount);
                let wi = 0;
                for (let n = startNode; n < endNode; ++n) {
                    const sub = nodeSubsets[n];
                    if (sub) {
                        chunkIndices.set(sub, wi);
                        wi += counts[n];
                    }
                }

                const chunkCols: Column[] = new Array(numCols);
                for (let ci = 0; ci < numCols; ++ci) {
                    const src = lodCols[ci].data as Float32Array;
                    const dst = new Float32Array(chunkSplatCount);
                    for (let i = 0; i < chunkSplatCount; ++i) dst[i] = src[chunkIndices[i]];
                    chunkCols[ci] = new Column(lodCols[ci].name, dst);
                }
                const chunkTable = new DataTable(chunkCols, lodDataTable.transform);

                const identity = new Uint32Array(chunkSplatCount);
                for (let i = 0; i < chunkSplatCount; ++i) identity[i] = i;

                const chunkIdx = chunkIdxByLevel[k]++;
                const firstNode = nodesAtD[startNode];
                const lodFileName = `${firstNode.id}.sog`;
                const chunkPath = `${name}/data/3dgs/${lodFileName}`;
                console.warn(`[LCC2] LOD${k}/chunk${chunkIdx}: nodes[${startNode}..${endNode - 1}] ${chunkSplatCount.toLocaleString()} splats → ${chunkPath}`);

                return { startNode, endNode, chunkTable, identity, lodFileName, chunkPath, chunkIdx };
            };

            const finalizeChunk = (info: { startNode: number; endNode: number; lodFileName: string; chunkIdx: number }) => {
                const fileIdx = splatFiles.length;
                splatFiles.push(`data/3dgs/${info.lodFileName}`);

                let nodeOffset = 0;
                for (let n = info.startNode; n < info.endNode; ++n) {
                    if (counts[n] > 0) {
                        nodeRefs.set(nodesAtD[n], { name: fileIdx, start: nodeOffset, count: counts[n] });
                        lodSplatsByLevel[k] += counts[n];
                    }
                    nodeOffset += counts[n];
                }
                (globalThis as { gc?: () => void }).gc?.();
            };

            let cursor = 0;
            let nextInfo = prepareChunk(cursor);
            while (nextInfo) {
                cursor = nextInfo.endNode;

                const writePromise = writeSogInternal({
                    filename: nextInfo.chunkPath,
                    dataTable: nextInfo.chunkTable,
                    bundle: true,
                    iterations,
                    createDevice: createGpuDevice,
                    indices: nextInfo.identity
                }, fs);
                const currentInfo = nextInfo;

                nextInfo = prepareChunk(cursor);

                await writePromise;
                finalizeChunk(currentInfo);
            }

            cumulativeSplats += levelTotal;
            const progressPercent = 5 + Math.round(95 * cumulativeSplats / totalEstimatedSplats);
            events?.fire('progressUpdate', {
                text: `LOD ${k} (${D}/${treeDepth}): ${levelTotal.toLocaleString()} splats  —  ${progressPercent}%`,
                progress: progressPercent
            });
            lodExportProgress = progressPercent;
        }

        for (let k = 0; k < treeDepth; ++k) lodSplats.push(lodSplatsByLevel[k]);

        const root = emitAdaptiveTreeJson(adaptiveRoot, nodeRefs, splatFiles);

        const totalSplats = lodSplats.reduce((a, b) => a + b, 0);

        const meta = {
            version: '0.0.3',
            name: name || 'ReSplat Lcc2 Splats',
            description: 'ReSplat exported LCC2 splats (multi-LOD combined)',
            guid,
            source: 'resplat',
            dataType: 'Editor',
            epsg: 0,
            offset: [0, 0, 0],
            shift: [0, 0, 0],
            scale: [1, 1, 1],
            fileType,
            totalSplats,
            lodSplats,
            totalLevels: treeDepth,
            splatType,
            virtualLoD: null as null,
            env: null as null,
            splatExtraAttributes: null as null,
            renderingHints: {
                renderMethod: 'splatting',
                renderMethodVariant: 'ewa',
                sortingMethod: 'depth',
                cameraModel: 'pinhole',
                colorSpace: 'srgb_rec709_display'
            },
            root
        };

        const jsonWriter = await fs.createWriter(`${name}/${name}.lcc2`);
        await jsonWriter.write(new TextEncoder().encode(JSON.stringify(meta, null, 2)));
        await jsonWriter.close();

        // ZIP fallback (same as serializeLcc2).
        if (fs instanceof MemoryFileSystem) {
            const downloadFs = new BrowserFileSystem(`${name}.zip`);
            const zipWriter = await downloadFs.createWriter(`${name}.zip`);
            const zipFs = new ZipFileSystem(zipWriter);
            try {
                for (const [filename, data] of fs.results.entries()) {
                    const writer = await zipFs.createWriter(filename);
                    await writer.write(data);
                    await writer.close();
                }
            } finally {
                await zipFs.close();
            }
        }
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    } finally {
        events?.fire('progressEnd');
    }
};

export {
    Writer,
    serializePly,
    serializePlyToWriter,
    serializePagedLod0Ply,
    serializePlyCompressed,
    serializeStandardPly,
    serializeSplat,
    serializeSog,
    serializeLcc2,
    serializeLcc2FromLodLog,
    serializeLcc2FromLodSplats,
    serializeViewer,
    AnimTrack,
    CameraPose,
    Camera,
    Annotation,
    PostEffectSettings,
    defaultPostEffectSettings,
    ExperienceSettings,
    SerializeSettings,
    SogSettings,
    Lcc2ExportOptions,
    ViewerExportSettings,
    SingleSplat,
    SplatTransformCache
};
